<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { DEFAULT_MAP_ID, MAP_SUMMARIES } from '@shared/maps';
import {
  DEFAULT_MATCH_MODE,
  DEFAULT_MISSION_DIFFICULTY,
  MATCH_MODES,
  MISSION_DIFFICULTIES,
  MISSION_KINDS,
  type MatchMode,
  type MissionDifficulty,
  type MissionKind,
} from '@shared/protocol';

import { api, type GarageItem, type ItemKind } from '@/net/api';

import BattlePicker from '@/garage/BattlePicker.vue';
import TrainingPrompt from '@/garage/TrainingPrompt.vue';
import {
  dismiss as dismissTraining,
  dismissed as trainingDismissed,
  needsTraining,
} from '@/garage/training-prompt';
import GarageLobby from '@/garage/GarageLobby.vue';
import HangarView from '@/garage/HangarView.vue';
import CustomiseWindow from '@/garage/CustomiseWindow.vue';
import GearIcon from '@/ui/GearIcon.vue';
import BrandLogo from '@/ui/BrandLogo.vue';
import ConfirmPurchase from '@/ui/ConfirmPurchase.vue';
import ConvertCreditsModal from '@/garage/ConvertCreditsModal.vue';
import DailyQuestsWindow from '@/garage/DailyQuestsWindow.vue';
import RewardPopup from '@/ui/RewardPopup.vue';
import NotificationBanner from '@/ui/NotificationBanner.vue';
import SettingsPanel from '@/ui/SettingsPanel.vue';
import ChatDock from '@/ui/ChatDock.vue';
import DiamondShop, { type Category as ShopCategory } from '@/garage/DiamondShop.vue';
const CommissionOffice = defineAsyncComponent(() => import('@/garage/CommissionOffice.vue'));
import { MARKET_TITLE, aboutOpen as officeAboutOpen } from '@/state/market';
import PaymentMethodPicker from '@/garage/PaymentMethodPicker.vue';
import FuelGauge from '@/garage/FuelGauge.vue';
import ItemCard from '@/garage/ItemCard.vue';
import NoTitleCard from '@/garage/NoTitleCard.vue';
import ItemWorkbench from '@/garage/ItemWorkbench.vue';
import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import { boostRemaining, formatCountdown } from '@/garage/format';
import { watchUpgradeInstalls } from '@/state/upgrade-installs';
import SpecialOfferPopup from '@/ui/SpecialOfferPopup.vue';
import {
  armSpecialOffers,
  featuredOffers,
  offerDue,
  takeDueOffer,
  type OfferSection,
  type SpecialOffer,
} from '@/state/special-offers';
import { purchaseRequest } from '@/state/confirm-purchase';
import { rewardReport } from '@/state/reward-popup';
import RankLadder from '@/garage/RankLadder.vue';
import ResearchTree from '@/garage/ResearchTree.vue';
import StatList from '@/garage/StatList.vue';
import TankPreview from '@/garage/TankPreview.vue';
import {
  HULL_STATS,
  SPEC_STATS,
  TURRET_STATS,
  formatBalance,
  formatCredits,
  specValues as resolveSpecValues,
} from '@/garage/stat-display';
import { liveryFor, STOCK_PAINT_ID } from '@shared/liveries';
import { askToBuy, type PurchaseArt, type PurchaseStats } from '@/state/confirm-purchase';
import { askSignOut } from '@/state/confirm-sign-out';
import { devicePromptOpen } from '@/state/device-prompts';
import { useGarage } from '@/state/garage';
import { showNotice } from '@/state/notification-banner';
import { focusResearch } from '@/state/research-focus';
import { groundQuality } from '@/state/settings';
import { useFavourites } from '@/state/favourites';
import { useSession } from '@/state/session';

/**
 * The garage.
 *
 * On the two shelves that fit parts to a tank, the left column is what you are
 * taking into the arena — the tank itself, rendered from its own meshes, and the
 * resolved spec — and the right column is the grid you are changing it from.
 * Keeping the spec on screen while shopping is the point: the numbers that matter
 * are the ones on the tank, not the ones on the shelf. Everything about *leaving*
 * for a battle lives on the garage floor; see `showSidebar`.
 */
/**
 * `deploy` carries which arena to land in and which map to fight on.
 *
 * `arenaId` undefined means quick match. **A mission is now the only thing that
 * ever names one** — the Arena tab used to, and it is gone (see `tabs`), so the
 * remaining caller is `launch()`, which opens a mission room over HTTP and then
 * joins it by id. `mapId` is only consulted when no arena is named — an arena's
 * map is fixed when it is created, so joining a room means taking its map, and
 * the picker below is about *which quick-match room to open* rather than about
 * reconfiguring one.
 */
const emit = defineEmits<{
  deploy: [arenaId?: string, mapId?: string, mode?: MatchMode];
  /**
   * Into the tutorial.
   *
   * Its own event rather than a `deploy` with a magic arena id, because it
   * carries neither of the two things a deploy carries: the tutorial is fought
   * on a fixed map under fixed rules, and the garage's picker has no say in it.
   *
   * It survived the tutorial becoming a mission, and had to. Every other mission
   * is opened by `POST /api/battle/missions` and then joined by id; the tutorial
   * keeps the `TUTORIAL_ARENA_ID` sentinel, because that route opens a **paid**
   * room and the tutorial is the one join that charges no fuel and banks
   * nothing. See `CREATABLE_MISSION_KINDS`.
   */
  tutorial: [];
  /**
   * Open the profile overlay.
   *
   * Raised rather than mounted here, because `App.vue` owns it: the *verification
   * gate* opens the same overlay, and that screen is not inside the garage. One
   * owner, so the two doors cannot end up opening two different components.
   */
  profile: [];
}>();

const session = useSession();
const garage = useGarage();

/**
 * Liveries are back, because the reason they left has been fixed.
 *
 * They were pulled — correctly — because the tab sold a paint that changed
 * nothing a player could see: the authored GLBs bring their own materials and
 * two hex columns on a catalogue row had nothing to bind to on a real mesh. *A
 * shop selling an invisible product is worse than no shop*, and that rule is
 * still the right one.
 *
 * What changed is the product. A livery is now a pattern projected onto the
 * mesh in object space, a finish, and the track belt the tank runs on — visible
 * in the arena, on the card, and on this screen. See `shared/liveries.ts` for
 * the design and `game/livery-material.ts` for why a colour could never have
 * done it.
 */
type Tab = 'garage' | 'hulls' | 'turrets' | 'cosmetics' | 'research' | 'shop' | 'commissions';
/**
 * The garage floor is where a player lands.
 *
 * It used to be the hulls tab, which opened the game on a shop rather than on
 * the thing the player owns. See `GarageLobby.vue`.
 */
const tab = ref<Tab>('garage');

/**
 * Which shelf the Shop tab opens on the *next* time it mounts.
 *
 * `DiamondShop` reads this once, as `initialCategory`, and keeps its own
 * `cat` ref from there — the shop is torn down and rebuilt on every tab
 * switch (`v-else-if`), so a prop read at mount is enough; there is no open
 * instance whose category needs pushing to after the fact. `null` is the
 * ordinary case and means Featured, the tab's own landing shelf.
 */
const shopCategory = ref<ShopCategory | null>(null);

/**
 * The Research tab's Research Lab strip: what the boost is doing while it is
 * live, and what it costs while it is not. Remaining time ticks on a local
 * 30-second clock — the server's expiry is the truth, this only draws it.
 */
const labClockMs = ref(Date.now());
let labClock: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  labClock = setInterval(() => {
    labClockMs.value = Date.now();
  }, 30_000);
});
onBeforeUnmount(() => {
  if (labClock) clearInterval(labClock);
});
const researchLabLeft = computed(() =>
  boostRemaining(wallet.value?.researchBoostExpiresAt ?? null, labClockMs.value),
);
const researchLabFrom = computed(() => {
  const offers = garage.shop.value?.boosts.filter((b) => b.kind === 'RESEARCH') ?? [];
  return offers.length ? Math.min(...offers.map((b) => b.diamondCost)) : null;
});

/** Opens the shop on a given shelf — the diamonds chip's "buy more" route. */
function openShop(category: ShopCategory): void {
  shopCategory.value = category;
  tab.value = 'shop';
}

/**
 * The special-offers popup. Which offer and when lives in `state/special-offers`;
 * this only decides whether *now* is a polite moment — never over another dialog,
 * never while something is mid-purchase — and otherwise leaves the offer due, so
 * it appears the moment the screen is clear.
 */
const specialOffer = ref<SpecialOffer | null>(null);
let offerWatch: ReturnType<typeof setInterval> | null = null;

function anotherDialogIsUp(): boolean {
  return (
    devicePromptOpen.value ||
    purchaseRequest.value !== null ||
    rewardReport.value !== null ||
    questsOpen.value ||
    rankLadderOpen.value ||
    trainingPromptOpen.value ||
    convertOpen.value ||
    officeAboutOpen.value ||
    !!garage.customiseOpen.value ||
    workbenchId.value !== null
  );
}

function checkSpecialOffer(): void {
  if (specialOffer.value || !offerDue() || anotherDialogIsUp()) return;
  if (!garage.shop.value || garage.busyId.value !== null) return;
  specialOffer.value = takeDueOffer(
    featuredOffers(garage.shop.value, garage.bundles.value, Date.now()),
  );
}

onMounted(() => {
  armSpecialOffers(session.player.value?.id ?? null);
  offerWatch = setInterval(checkSpecialOffer, 5_000);
});
onBeforeUnmount(() => {
  if (offerWatch) clearInterval(offerWatch);
});

/** Asks first — a re-roll spends diamonds — then does it. */
async function rerollQuests(): Promise<void> {
  const quests = garage.quests.value;
  if (!quests) return;
  const accepted = await askToBuy({
    item: 'a new set of daily quests',
    verb: 'Re-roll',
    detail: 'Unfinished quests are swapped for new ones. Finished quests stay.',
    price: quests.rerollCost,
    currency: 'diamonds',
    confirmLabel: 'Re-roll',
  });
  if (accepted) await garage.rerollQuests();
}

function offerView(section: OfferSection): void {
  specialOffer.value = null;
  openShop(section);
}

async function offerBuy(offer: SpecialOffer): Promise<void> {
  specialOffer.value = null;
  if (offer.kind === 'bundle') await garage.buyBundle(offer.id, offer.offerId);
  else if (offer.kind === 'boost') await garage.buyBoost(offer.id);
}

/**
 * The sidebar belongs to the two tabs that fit parts to a tank, and to no other.
 *
 * It *is* the shop's chrome — the tank you are fitting, next to the grid you are
 * fitting it from — so the question it answers is *what am I comparing this card
 * against*. Only hulls and turrets ask it. A livery and a title change nothing it
 * shows, research is a map, the shop sells currency and boosts, the Arena tab is
 * a list of rooms and the tutorial is a page of prose; on all six the panel was
 * a column of numbers about something the player was not looking at, taking a
 * third of the window from something they were.
 *
 * The garage floor has always been excluded and for a different reason: it
 * carries its own readout down its right edge and needs the rest of the window
 * for the vehicle, so showing both would be two copies of the same numbers
 * either side of a squeezed picture.
 */
/*
 * `!compact` is folded in **here** rather than at the `<aside>`, and that is the
 * load-bearing half. `.body`'s `full` class is `!showSidebar`, and `.body` is a
 * two-column grid whose first column is `minmax(260px, 320px)` — so hiding the
 * aside without clearing that class does not leave a gap, it puts the *grid* in
 * the narrow column and renders the whole shelf inside 320px. One condition, both
 * consequences.
 */
const showSidebar = computed(
  () => (tab.value === 'hulls' || tab.value === 'turrets') && !compact.value,
);

/**
 * The garage floor, and the only tab that is a *room* rather than a document.
 *
 * Deliberately **not** `!showSidebar` any more, though it was exactly that while
 * the lobby was the one sidebar-less tab. It turns the title bar into a scrim
 * *over* the content so the hangar reaches all four edges of the window — which
 * is right for a full-bleed WebGL scene and wrong for every other tab, where it
 * would drop a research tree or a shop grid underneath the bar.
 */
const immersive = computed(() => tab.value === 'garage');

/**
 * The hangar, owned here so it survives the tabs.
 *
 * It used to be the lobby's, torn down on every tab change per the context
 * budget — and rebuilt from a cold start on every return, which is a GLB
 * re-instantiation and a backdrop fetch to show the same room. Owned by the
 * screen it is one context for the whole garage session: every tab sits on
 * top of it (see `.stage` and the scrim below), and a non-garage tab drops it
 * to a background tier — quarter pixels, a quarter of the frames, no pointer —
 * rather than to nothing. The budget's hard rule is untouched: deploying
 * unmounts this screen, which disposes the scene before `ArenaScene` asks for
 * its own context, and `garage-hangar` still measures exactly one in the
 * arena.
 */
const hangar = ref<{
  report: () => unknown;
  resetView: () => void;
  setFocus?: (
    fogStart: number,
    fogEnd: number,
    spotIntensity: number,
    fogGrey?: number,
  ) => void;
} | null>(null);

/**
 * The hangar's tier, taken from the graphics setting the player already made.
 *
 * A separate garage-quality control would be a second dial for the same
 * decision. Anyone who dropped the ground to `grid` to keep the arena playable
 * has told us their machine is short, and the garage should not be the thing
 * that ignores it.
 */
const hangarQuality = computed<'high' | 'low'>(() =>
  groundQuality.value === 'grid' ? 'low' : 'high',
);

const hangarHull = computed(() => garage.equippedHull.value ?? null);
const hangarTurret = computed(() => garage.equippedTurret.value ?? null);

const favourites = useFavourites();

/**
 * The dev probe. Registered while the *screen* is mounted — the hangar's own
 * lifetime now — and removed with it, so a stale probe cannot report on a
 * disposed scene from inside the arena. The bay fields stay because
 * `garage-bays` reads them; they come from the same module-scope stores the
 * lobby reads.
 */
onMounted(() => {
  (window as unknown as Record<string, unknown>).__sowFocus = (
    fogStart: number,
    fogEnd: number,
    spotIntensity: number,
    fogGrey?: number,
  ) => hangar.value?.setFocus?.(fogStart, fogEnd, spotIntensity, fogGrey);
  (window as unknown as Record<string, unknown>).__sowGarage = () => ({
    hangar: hangar.value?.report() ?? null,
    bays: favourites.slots.value.length,
    selectedBay: favourites.indexOf(
      hangarHull.value && hangarTurret.value
        ? { hullId: hangarHull.value.id, turretId: hangarTurret.value.id }
        : null,
    ),
  });
});
onBeforeUnmount(() => {
  delete (window as unknown as Record<string, unknown>).__sowGarage;
  delete (window as unknown as Record<string, unknown>).__sowFocus;
});

/**
 * The customise window, open over the garage rather than as another tab.
 *
 * A hull's parts are a property of *that hull*, so the screen it opens from is
 * the one showing it — putting them in a tab of their own would lose the link
 * between the tank being looked at and the parts being fitted to it. Which hull
 * it is showing lives in the store rather than here, because a purchase made
 * inside the window has to rebuild it from the server's answer.
 *
 * Two ways in, one destination: the sidebar button opens the hull being driven,
 * a card's own button opens that card's hull.
 */
const tabs: Array<{ id: Tab | 'tanks'; label: string }> = [
  { id: 'garage', label: 'Garage' },
  // The Arena tab is gone, and unlike the Tutorial tab below it did not move
  // anywhere — it was retired as obsolete. It listed the running rooms and joined
  // one by id, which answered "get me into *that* fight" back when a room was a
  // thing you picked out of a lobby; the battle picker answers the question
  // anybody actually has ("get me into a fight") and resolves quick match from
  // the map and the mode, so a bare deploy is one request and two players who
  // choose the same map land in the same derived room without either of them
  // naming it.
  //
  // `ArenaBrowser.vue` and the lobby endpoints it read (`GET`/`POST
  // /api/battle/arenas`) are deliberately left in place: the component is
  // unreferenced rather than deleted, and the routes are still what
  // `POST /api/battle/missions` and the e2e suite build rooms with. Nothing about
  // the netcode moved.
  // The Tutorial tab is gone, and it went where it belongs: the tutorial is a
  // *mission*, so it is the first card on the battle picker's Missions tab
  // beside Convoy Run, Relay Raid and Last Stand. A tab of prose describing four
  // lessons was the right shape while the tutorial was a private deathmatch with
  // an overlay on it; a scripted PvE fight with an objective strip and a result
  // screen is a mission and is chosen where missions are chosen. It also gets
  // the completion badge for free, which a tab could never have had.
  // Hulls and turrets share one Tanks tab, switched inside by `tankTabs`.
  { id: 'tanks', label: 'Tanks' },
  // One tab for both cosmetics, next to the two things they decorate. Liveries
  // and Titles each had a tab of their own; neither shelf is large (nine paints,
  // ten titles) and both answer the same question — *how do I look* — so two
  // half-empty tabs became two labelled shelves on one. The order inside keeps
  // the old tab order's logic: a livery is a change to the vehicle and a title
  // is a change to the player, so paint comes before name.
  { id: 'cosmetics', label: 'Cosmetics' },
  { id: 'research', label: 'Research' },
  { id: 'shop', label: 'Shop' },
  { id: 'commissions', label: MARKET_TITLE },
];

/** The two shelves under the Tanks tab, and which one it reopens on. */
const tankTabs: Array<{ id: 'hulls' | 'turrets'; label: string }> = [
  { id: 'hulls', label: 'Hulls' },
  { id: 'turrets', label: 'Turrets' },
];
const lastTankTab = ref<'hulls' | 'turrets'>('hulls');
watch(tab, (next) => {
  if (next === 'hulls' || next === 'turrets') lastTankTab.value = next;
});
const isTopTab = (id: Tab | 'tanks'): boolean =>
  id === 'tanks' ? tab.value === 'hulls' || tab.value === 'turrets' : tab.value === id;
function openTopTab(id: Tab | 'tanks'): void {
  tab.value = id === 'tanks' ? lastTankTab.value : id;
  menuOpen.value = false;
}

/**
 * The tablet menu. At tablet widths the tab strip folds behind a burger button
 * (CSS decides when, see `.burger`); this only holds whether it is open, and
 * closes it on Escape or a click anywhere outside it.
 */
const menuOpen = ref(false);
function onMenuOutside(event: PointerEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target?.closest('.menu-panel, .burger')) menuOpen.value = false;
}
function onMenuKey(event: KeyboardEvent): void {
  if (event.key === 'Escape') menuOpen.value = false;
}
onMounted(() => {
  document.addEventListener('pointerdown', onMenuOutside);
  document.addEventListener('keydown', onMenuKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onMenuOutside);
  document.removeEventListener('keydown', onMenuKey);
});

const specValues = computed(() => resolveSpecValues(garage.spec.value));

const lifetime = computed(() => garage.overview.value?.stats ?? null);
const wallet = computed(() => garage.wallet.value);

/**
 * The deploy button is a courtesy, not the gate.
 *
 * The gate is `PlayerService.openSession`, which charges the fuel inside the
 * same transaction that opens the sortie. If this button were somehow clicked
 * on an empty tank the socket would be told no — this only saves the player the
 * round trip and explains why.
 *
 * It defaults to *true* while the wallet is still loading, deliberately. An
 * unknown tank drawn as an empty one would tell every player they are out of
 * fuel for as long as the overview takes to arrive, and the server refuses a
 * deploy this is wrong about.
 */
const canDeploy = computed(() => wallet.value?.canDeploy ?? true);

/**
 * A phone held sideways, which this garage does not fit.
 *
 * ── Why a viewport query and not the device ─────────────────────────────────
 *
 * The battle HUD gates its compact mode on `hud.touch`, because there the
 * trigger is *are there thumbs in the bottom corners* — a fact about the device.
 * Here the trigger is *is there room*, which is a fact about the viewport, and
 * the measurements say so plainly. On an iPad at 1180×820 this screen is fine:
 * the title bar is 13% of the height and the loadout sidebar sits *beside* the
 * grid at 320×674. On a phone at 667×375 the same bar is **43%** of the height
 * and the sidebar is **627×1078 stacked above** the grid, so the first card is
 * 1,258px down the page. A device check would strip a working tablet layout;
 * a viewport check does not.
 *
 * ── Why height ─────────────────────────────────────────────────────────────
 *
 * Landscape is the only orientation this game runs in, so the short edge is
 * always the height, and it is the height that runs out: 375–430px on phones
 * against 820 on a tablet and 700+ on any desktop window somebody plays in. One
 * threshold separates all three, where a width threshold would have to sit
 * between 844 and 1180 and would also catch a half-screen desktop window.
 *
 * ── Why the state lives in JS ──────────────────────────────────────────────
 *
 * Because a plain `@media` block cannot drive a `v-if`, and the sidebar has to
 * actually not render rather than be hidden — hidden, it still lays out and still
 * asks the thumbnail cache for a preview nobody can see. So `matchMedia` is the
 * single source of truth and every rule keys off `.garage.compact`, which is the
 * same arrangement `GameHud` uses one screen along.
 */
const COMPACT_QUERY = '(max-height: 520px)';
const compact = ref(false);
let compactMedia: MediaQueryList | null = null;
const onCompactChange = (e: MediaQueryListEvent | MediaQueryList) => {
  compact.value = e.matches;
};
onMounted(() => {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  compactMedia = window.matchMedia(COMPACT_QUERY);
  onCompactChange(compactMedia);
  compactMedia.addEventListener('change', onCompactChange);
});
onBeforeUnmount(() => compactMedia?.removeEventListener('change', onCompactChange));

/**
 * The worn rank's insignia, or null.
 *
 * Derived from the rank number, zero-padded, exactly as `RankLadder.artFor`
 * derives it — the convention is the ladder's and this is the second reader of
 * it, not a second convention. A path is not a fact worth sending, and a served
 * one would be a third place for the naming to live.
 *
 * Cleared whenever the rank changes, or one missing file would blank the badge
 * for every rank reached after it — the same reset the map thumbnail's own
 * missing-art flag used to carry.
 */
const rankArtMissing = ref(false);
watch(
  () => wallet.value?.rank,
  () => {
    rankArtMissing.value = false;
  },
);
const rankArt = computed(() => {
  const rank = wallet.value?.rank;
  if (!rank || rankArtMissing.value) return null;
  return `/assets/ranks/rank-${String(rank).padStart(2, '0')}.webp`;
});

/**
 * Fuel is no longer asked for here.
 *
 * It used to be a single tier, so one function here could word the whole
 * confirmation from the wallet alone. There are three tiers now, each its own
 * price, and `DiamondShop`'s fuel cards are the only place all three prices
 * and the wallet's current fuel are already in hand — so the shelf asks its
 * own question (`buyFuel` in `DiamondShop.vue`) and this screen only forwards
 * the units it settled on to the store. See the `refill` wiring below.
 */

// ─── Every spend asks first ──────────────────────────────────────────────────

/**
 * The three purchases a catalogue card can raise, confirmed in one place.
 *
 * `ItemCard` emits and never asks, which is what keeps this to three call sites
 * rather than three per card: the grid renders twenty of them, so a dialog call
 * inside the card would be twenty copies of the same two lines with twenty
 * chances for the wording to drift apart. Equipping and customising are
 * deliberately absent — they cost nothing and are freely reversible, and a
 * modal in front of *try this turret on* is friction protecting nothing.
 *
 * A spend whose subject or price this screen cannot state goes through
 * **unasked** rather than being refused here. Both cases are unreachable from a
 * rendered card — the id came out of this same list, and the upgrade and
 * top-tier buttons are only drawn once their cost is known — and a confirmation
 * that silently swallowed a purchase would have become the place a rule is
 * enforced, which is the one thing it may never be. The server decides; this
 * only asks.
 */
const ITEM_NOUN: Record<ItemKind, string> = {
  HULL: 'hull',
  TURRET: 'turret',
  PAINT: 'paint',
  TITLE: 'title',
};

/**
 * Which shelf the grid is showing, and what a card is being compared against.
 *
 * Computeds rather than the ternaries these replaced. The old pair defaulted to
 * *turrets* for any tab that was not `hulls`, which was correct while there were
 * two shelves and silently wrong the moment there were three: a Titles tab would
 * have rendered the turret list and equipped through `equipPaint`, and the
 * server would have answered "No such livery" for a request nobody could see was
 * malformed.
 */
const gridItems = computed(() => {
  if (tab.value === 'hulls') return garage.hulls.value;
  return garage.turrets.value;
});

/**
 * The Cosmetics tab is two labelled shelves on one page — liveries, then
 * titles — rather than one mixed grid: the two kinds preview differently (a
 * texture tile against a nameplate mock), and interleaving them reads as a
 * drawer somebody tipped out. Paint before name, the old tab order's logic: a
 * livery is a change to the vehicle and a title is a change to the player.
 * Neither kind has a stat to differ by, which is the whole point of a
 * cosmetic — so these cards get no "against" column at all.
 */
const cosmeticShelves = computed(() => [
  { key: 'liveries', title: 'Liveries', items: garage.paints.value },
  { key: 'titles', title: 'Titles', items: garage.titles.value },
]);

/**
 * Which cosmetic shelf is on screen.
 *
 * The tab reuses the store's container — a category rail down the left and one
 * shelf at a time — rather than stacking both shelves on one scrolling page. Two
 * shelves fitted on a page while there were nine liveries; at twenty-one paints
 * plus ten titles a stacked page buries the second shelf below the fold and makes
 * the first one a wall, which is the exact problem the store's rail was built for.
 *
 * A ref rather than a route: the same argument `authView` makes one level up. Two
 * views with one transition between them is a boolean, and making it a route would
 * put the garage's live hangar scene behind a router transition for the sake of a
 * shelf switch.
 */
const cosmeticCat = ref<'liveries' | 'titles'>('liveries');

/** The shelf the rail has selected. Never undefined — the ref is a union. */
const cosmeticShelf = computed(
  () => cosmeticShelves.value.find((s) => s.key === cosmeticCat.value)!,
);

/**
 * The card's "against" column — what is fitted now, so the grid can show the
 * difference. Only the hull and turret shelves have one; see the cosmetics
 * note above.
 */
const gridCurrent = computed(() => {
  if (tab.value === 'hulls') return garage.equippedHull.value;
  return garage.equippedTurret.value;
});

/** The title being worn, or null. Drives the "wear none" control. */
const wornTitleId = computed(() => garage.overview.value?.player.equippedTitleId ?? null);

/**
 * One dispatcher rather than a ternary in the template, for the reason above:
 * the failure mode of getting this wrong is a request the server refuses with a
 * message about a slot the player was not looking at.
 */
async function equipFromGrid(id: string): Promise<void> {
  // By the item's own kind rather than by the tab, because the Cosmetics tab
  // holds two kinds at once — a tab-shaped dispatch would send every title
  // through `equipPaint` and the server would answer "No such livery" for a
  // request nobody could see was malformed.
  const kind = itemById(id)?.kind;
  if (kind === 'HULL') void garage.equipHull(id);
  else if (kind === 'TURRET') void garage.equipTurret(id);
  else if (kind === 'TITLE') void garage.equipTitle(id);
  else {
    // **Reverting to the free coat throws a paid paint away**, and it is the only
    // free action in the garage that destroys something. It costs nothing, so it
    // does not go through `askToBuy`'s price shape — but it is exactly the
    // mis-click that dialog exists to prevent, so it asks anyway with the price
    // set to zero and the confirm button saying what it does rather than "Buy".
    //
    // Only when there is something to lose: reverting from the stock coat to the
    // stock coat is a no-op nobody needs to confirm.
    const losing = id === STOCK_PAINT_ID ? wornPaint.value : null;
    if (
      losing &&
      !(await askToBuy({
        item: 'the issued coat',
        detail: `${losing.name} is a one-time paint job — reverting does not keep it, and wearing it again means buying it again`,
        price: 0,
        currency: 'credits',
        confirmLabel: 'Strip the paint',
      }))
    ) {
      return;
    }
    void garage.equipPaint(id);
  }
}

function itemById(id: string): GarageItem | null {
  return garage.overview.value?.items.find((item) => item.id === id) ?? null;
}

/**
 * The picture for one catalogue item, by its kind.
 *
 * One resolver for all three of this screen's spends — buy, upgrade, top tier —
 * because they are all the same four kinds of item and a picture chosen per call
 * site is three places for a paint to end up showing a hull. Every path is the
 * one the *card* already uses, so the dialog and the card behind it cannot
 * disagree about what the thing looks like:
 *
 * - a **hull** or **turret** is a rendered still of the real meshes, drawn on the
 *   player's own other half so it answers "what would mine look like" exactly as
 *   the card does;
 * - a **livery** is the pattern texture, which is what the shop shows and cannot
 *   be photographed wearing the wrong paint;
 * - a **title** is its badge, drawn rather than fetched.
 *
 * A livery with no pattern — the factory olive — has nothing to show, so it
 * answers `undefined` and the dialog is the text-only one rather than an empty
 * plate.
 */
function artFor(item: GarageItem): PurchaseArt | undefined {
  const player = garage.overview.value?.player;
  if (item.kind === 'HULL') {
    return {
      kind: 'model',
      hullId: item.id,
      turretId: player?.equippedTurretId ?? null,
      framing: 'hull',
    };
  }
  if (item.kind === 'TURRET') {
    return {
      kind: 'model',
      hullId: player?.equippedHullId ?? null,
      turretId: item.id,
      framing: 'turret',
    };
  }
  if (item.kind === 'TITLE') return { kind: 'title', titleId: item.id };
  const pattern = liveryFor(item.id).pattern;
  return pattern ? { kind: 'image', src: pattern } : undefined;
}

/**
 * The stat difference between two server-resolved readings of the same item.
 *
 * `next` is `nextStats` or `nextPrestigeStats` — both resolved by the server's own
 * `statsOf`, one level along from `currentStats`. **Nothing here computes a
 * level**; the browser only picks which descriptor table to label the figures
 * with, and a kind with no stats at all (a paint, a title) answers `undefined`,
 * which is what keeps a cosmetic's dialog free of an empty bar chart.
 */
function statsFor(item: GarageItem, next: Record<string, number> | null): PurchaseStats | undefined {
  if (!next) return undefined;
  const descriptors =
    item.kind === 'HULL' ? HULL_STATS : item.kind === 'TURRET' ? TURRET_STATS : null;
  if (!descriptors) return undefined;
  return { descriptors, values: next, against: item.currentStats };
}

/**
 * The paint the player is wearing now, for the consumable confirmation below.
 * `null` when it is the free coat, which is the case where nothing is lost.
 */
const wornPaint = computed(() => {
  const id = garage.overview.value?.player.equippedPaintId ?? null;
  if (!id || id === STOCK_PAINT_ID) return null;
  return garage.paints.value.find((p) => p.id === id) ?? null;
});

/**
 * Buys an item, and reports whether it actually happened.
 *
 * The boolean is what lets the workbench close itself on a purchase and stay open
 * on a refusal — a modal that closed either way would take the server's message
 * ("Light Frames must be unlocked first") off screen with it, which is the one
 * sentence worth reading at that moment. Declining the confirmation is also a
 * `false`: the player said no, and the window they said it from should still be
 * there.
 */
async function buyItem(id: string): Promise<boolean> {
  const item = itemById(id);
  // **A paint job is consumed, and the dialog has to say so.** This is the one
  // purchase in the game where the wording is the only thing standing between an
  // honest sale and a player who feels robbed: there is no `PlayerItem` row, so
  // what is bought cannot be re-applied later, and buying a second paint loses the
  // first. Naming the paint being lost rather than saying "your current paint" is
  // the difference between a warning somebody reads and one they skim.
  const consumable = item?.kind === 'PAINT' && item.id !== STOCK_PAINT_ID;
  const losing = consumable ? wornPaint.value : null;
  const paintDetail = consumable
    ? losing
      ? `One-time paint job — replaces ${losing.name}, which is not kept`
      : 'One-time paint job — applied when bought, not kept in a locker'
    : null;

  if (
    item &&
    !(await askToBuy({
      item: item.name,
      detail: paintDetail ?? `Tier ${item.tier} ${ITEM_NOUN[item.kind]}`,
      art: artFor(item),
      // The currency the *server* will charge, read off the same field the
      // server branches on. A premium item keeps a credit `price` that is a
      // valuation and is never debited, so quoting it here would name a figure
      // the player is not being asked for — the same failure `priceLabel`
      // exists to prevent on the diamond packs.
      ...(item.diamondPrice !== null
        ? { price: item.diamondPrice, currency: 'diamonds' as const }
        : { price: item.price, currency: 'credits' as const }),
    }))
  ) {
    return false;
  }
  await garage.purchase(id);
  // `mutate` clears `error` before the call and sets it only on a refusal, so this
  // is the store's own verdict rather than a second guess at it.
  return garage.error.value === null;
}

async function upgradeItem(id: string): Promise<void> {
  const item = itemById(id);
  const price = item?.nextUpgradeCost ?? null;
  if (
    item &&
    price !== null &&
    !(await askToBuy({
      item: item.name,
      // The level being *bought*, not the one the item is on. A player pressing
      // Upgrade on a level-2 turret is buying level 3, and a dialog saying
      // "level 2" would name the thing they already have.
      detail:
        item.nextUpgradeSeconds !== null
          ? `Upgrade to level ${item.level + 1} · installs in ${formatCountdown(item.nextUpgradeSeconds)}`
          : `Upgrade to level ${item.level + 1}`,
      art: artFor(item),
      // What the level actually buys. The card shows one number per stat; this is
      // the only place the *change* appears, which is what makes it worth the
      // room here and not on a hull purchase — see `PurchaseRequest.stats`.
      stats: statsFor(item, item.nextStats),
      price,
      currency: 'credits',
    }))
  ) {
    return;
  }
  await garage.upgrade(id);
}

/** Installs a finished upgrade — free; the level arrives with it. */
async function installUpgrade(id: string): Promise<void> {
  await garage.installUpgrade(id);
}

/**
 * Finishes a running install for diamonds, after the same confirmation every
 * other spend gets. The figure is the server's quote at the last read, so the
 * charge can only be at or below it.
 */
async function skipUpgrade(id: string): Promise<void> {
  const item = itemById(id);
  const install = item?.upgrade ?? null;
  if (!item || !install || install.ready) return;
  const accepted = await askToBuy({
    item: item.name,
    detail: `Finish level ${install.toLevel} now, skipping ${formatCountdown(install.secondsRemaining)}`,
    art: artFor(item),
    stats: statsFor(item, item.nextStats),
    price: install.skipDiamondCost ?? 0,
    currency: 'diamonds',
    confirmLabel: 'Finish now',
  });
  if (accepted) await garage.skipUpgrade(id);
}

/**
 * Watches pending installs: re-reads when a countdown runs out, and announces a
 * ready one with a banner whose Install button opens that item's workbench.
 * `installsSnapshotAt` moves with every fresh overview, which is what the
 * countdown is aged from.
 */
const installsSnapshotAt = ref(Date.now());
watch(
  () => garage.overview.value?.items,
  () => (installsSnapshotAt.value = Date.now()),
);
let stopInstalls: (() => void) | null = null;
onMounted(() => {
  stopInstalls = watchUpgradeInstalls({
    items: () => garage.overview.value?.items,
    snapshotAt: () => installsSnapshotAt.value,
    refresh: () => garage.refresh(),
    open: (id) => (workbenchId.value = id),
  });
});
onBeforeUnmount(() => stopInstalls?.());

async function prestigeItem(id: string): Promise<void> {
  const item = itemById(id);
  const price = item?.nextPrestigeCost ?? null;
  if (
    item &&
    price !== null &&
    !(await askToBuy({
      item: item.name,
      detail: `Top tier, level ${item.prestige + 1}`,
      art: artFor(item),
      stats: statsFor(item, item.nextPrestigeStats),
      price,
      currency: 'diamonds',
    }))
  ) {
    return;
  }
  await garage.prestige(id);
}

/**
 * A locked card's research gate is a link, and this is the far end of it.
 *
 * The card knows which node it needs and this screen owns which tab is showing,
 * so the card emits and the screen navigates. A card setting `tab` itself would
 * make every one of the twenty in the grid a second owner of that ref, and the
 * first layout change that moved the tabs would have twenty places to find.
 *
 * `focusResearch` is what survives the switch: the tree is not mounted at the
 * moment this runs and there is no parent between the two tabs to thread a prop
 * through. See `state/research-focus.ts` — the highlight is a nudge, and the
 * tree renders identically with or without one.
 */
function showResearch(nodeId: string): void {
  focusResearch(nodeId);
  tab.value = 'research';
}

// ─── The map picker ──────────────────────────────────────────────────────────

/**
 * The maps, read straight out of `@shared/maps`.
 *
 * Not fetched, and deliberately so: a map is *already* shared data — that is the
 * whole reason a hundred buildings cost nothing per snapshot, because both sides
 * look the definition up rather than sending it. A picker that asked the server
 * for the list would be a round trip to learn something the bundle already
 * contains, and would still have to agree with this file to draw anything.
 */
const maps = MAP_SUMMARIES;

/** Where the choice is remembered between sessions. */
const MAP_STORAGE_KEY = 'sow.map';

/**
 * The chosen map.
 *
 * Persisted in `localStorage` beside the graphics and aim settings, and for the
 * same reason: which map somebody likes is a fact about their browser, not about
 * their account, and it has no business on the wire or in Postgres. An entry
 * naming a map that no longer exists falls back to the default rather than
 * leaving the button pointing at nothing.
 */
const mapId = ref<string>(readStoredMap());

function readStoredMap(): string {
  try {
    const stored = window.localStorage.getItem(MAP_STORAGE_KEY);
    if (stored && maps.some((m) => m.id === stored)) return stored;
  } catch {
    // Storage can be denied outright. A picker that threw would take the whole
    // garage with it, over a preference.
  }
  return DEFAULT_MAP_ID;
}

watch(mapId, (id) => {
  try {
    window.localStorage.setItem(MAP_STORAGE_KEY, id);
  } catch {
    // See above. Forgetting the choice is a small cost; failing to deploy is not.
  }
});

/** Whether the battle dialog is up. See `BattlePicker.vue` for why it is one. */
const pickerOpen = ref(false);

/**
 * Whether the rank ladder is up.
 *
 * Local to the screen, unlike the customise window: nothing in the ladder can be
 * bought, so no mutation has to rebuild it while it is open and there is no
 * reason for the store to know it exists. It re-reads its rows from the overview
 * on every render, so a promotion arriving while it is open shows up anyway.
 */
const rankLadderOpen = ref(false);
/**
 * Whether the convert-to-credits modal is up.
 *
 * Local to the screen, same shape as `rankLadderOpen`: nothing about it needs
 * to survive a mutation the way the customise window does, because a convert
 * is keyed on its own amount (`convert_${diamonds}`) and does not rebuild
 * anything else the screen owns.
 */
const convertOpen = ref(false);
/** The settings window, closed by default — it is a control, not a readout. */
const settingsOpen = ref(false);

/**
 * Escape closes it, so the window's own `Esc closes this` line is true on both
 * screens rather than only in the arena.
 *
 * The listener is here rather than inside `SettingsPanel` because the battle
 * HUD already owns a window-level Escape handler that *toggles* its menu: a
 * second one inside the panel would fire on the same keypress and cancel it
 * out, leaving a menu that cannot be shut. The key belongs to whoever owns the
 * open/closed flag.
 *
 * On `window`, and gated on the flag rather than being added and removed with
 * it — one listener for the life of the screen is cheaper than a watcher, and
 * a closed window has nothing to close.
 */
function onEscape(event: KeyboardEvent): void {
  if (event.code !== 'Escape' || event.repeat || !settingsOpen.value) return;
  event.preventDefault();
  settingsOpen.value = false;
}
onMounted(() => window.addEventListener('keydown', onEscape));
onBeforeUnmount(() => window.removeEventListener('keydown', onEscape));

/**
 * Whether the daily-quest window is up.
 *
 * Local to the screen, like the rank ladder and unlike the customise window. A
 * claim *is* a mutation, but it answers with the whole overview and the window
 * renders straight off `garage.quests` — so it rebuilds itself from the store's
 * new value with nothing to keep in step, and there is no reason for the store
 * to know the window exists.
 */
/**
 * The title bar's own height, measured, and published to the tab below as
 * `--bar-h`.
 *
 * In immersive mode the bar is a **scrim over** the garage tab rather than a bar
 * above it, so it reserves no space and whatever is drawn at the top of that tab
 * has to leave some. The lobby did that with a fixed `padding-top`, and a fixed
 * figure cannot be right, and it now takes four values rather than three:
 * measured, the bar is **85 px at or above 2000 px** where `--bar-scale` is 1.3,
 * **66 px** on a one-row window under it, **100 px below 1400 px** where the tab
 * strip takes its own row, and **54 px** on a phone — and the fixed figure was
 * 8 px short even at 1440, which the one-line mode/map readout that used to sit
 * there absorbed invisibly. The daily-quest card does not: it is 140 px of
 * *control*, and the bar is `z-index: 3` against the tab's 1, so every pixel of
 * overlap is a click that lands on a tab instead of opening the quest window.
 *
 * Measured rather than restated as media queries, because the bar's height is a
 * consequence of its own contents and now of `--bar-scale` as well — a query
 * cannot read a custom property, so a restated figure would be wrong on the wide
 * tier by construction rather than merely eventually. A `ResizeObserver` cannot
 * drift.
 *
 * `undefined` until the first measurement, which leaves the consumer's own
 * fallback in play rather than collapsing the padding to zero for a frame.
 */
const barEl = ref<HTMLElement | null>(null);
const barHeight = ref(0);
let barObserver: ResizeObserver | null = null;

onMounted(() => {
  if (!barEl.value || typeof ResizeObserver === 'undefined') return;
  barObserver = new ResizeObserver(() => {
    barHeight.value = barEl.value?.offsetHeight ?? 0;
  });
  barObserver.observe(barEl.value);
  barHeight.value = barEl.value.offsetHeight;
});

onBeforeUnmount(() => {
  barObserver?.disconnect();
  barObserver = null;
});

/**
 * The one-time nudge into basic training. Rules and reasoning: `training-prompt.ts`.
 *
 * Two facts, one derived and one remembered, kept apart on purpose. `needsTraining`
 * reads the overview — never entered an arena, never beaten the tutorial — so the
 * prompt cannot outlive the player's first sortie whatever storage does;
 * `trainingDismissed` is the courtesy on top. **Both are re-evaluated on every
 * render rather than latched at mount**, because the overview arrives after this
 * component does: latched, a fresh account would be judged against a garage that
 * had not answered yet and would never be asked at all.
 *
 * `trainingSaidNo` is what makes *Not now* take effect in this tab without waiting
 * for storage to be read back — `trainingDismissed` is the source of truth on the
 * next load, and this is the one that closes the dialog now.
 */
const trainingSaidNo = ref(false);

const playerKey = computed(
  () => session.player.value?.id ?? session.player.value?.nickname ?? 'anon',
);

const trainingPromptOpen = computed(() => {
  if (trainingSaidNo.value) return false;
  // Never underneath a question about the device. `RotatePrompt` and
  // `FullscreenPrompt` are modal and sit far higher in the stacking order, so a
  // prompt opened behind one is a prompt whose buttons are visible through a
  // scrim and dead to the touch that reaches them — which is exactly what a new
  // phone player met, because the fullscreen offer and this one become true on
  // the same frame. Both come back the moment the device question is answered.
  // See `state/device-prompts.ts`.
  if (devicePromptOpen.value) return false;
  const overview = garage.overview.value;
  if (!overview) return false;
  // Never over another dialog, and never over a tab that is not the garage floor.
  // A player who has opened the shop or the research tree is exploring, and a
  // modal landing on top of that reads as an error rather than as a welcome.
  if (tab.value !== 'garage') return false;
  if (!needsTraining({
    sessionsJoined: overview.stats?.sessionsJoined ?? null,
    missionsBeaten: overview.missions.map((run) => run.kind),
  })) {
    return false;
  }
  return !trainingDismissed(playerKey.value);
});

function declineTraining(): void {
  trainingSaidNo.value = true;
  dismissTraining(playerKey.value);
}

/**
 * Take the prompt's offer.
 *
 * Dismissed on the way out as well as started, so a player who runs training and
 * leaves it half way is not asked again — they have seen the offer and acted on
 * it. The derived half would let them through otherwise: the tutorial banks no
 * session and marks nothing unless it is finished, which is exactly the case
 * `needsTraining` cannot distinguish and this line covers.
 */
function acceptTraining(): void {
  declineTraining();
  emit('tutorial');
}

const questsOpen = ref(false);

/**
 * Which item the workbench is showing, by **id** rather than by value.
 *
 * That is the load-bearing half. The grid is live — a purchase, an upgrade or a
 * fitting re-reads the whole overview and replaces every `GarageItem` object —
 * so a window holding the item it was opened with would go on showing the price,
 * the level and the ladder as they were *before* the purchase it just made. An id
 * re-resolves against the current list on every render, so buying from inside the
 * workbench updates the workbench.
 *
 * Local to the screen, like the rank ladder and unlike the customise window:
 * there is nothing here the store needs to know exists, because everything it
 * shows is derived from `garage.overview`.
 */
const workbenchId = ref<string | null>(null);

/**
 * The item the workbench is showing, or null.
 *
 * Resolved out of the same lists the grids render, so it cannot disagree with the
 * card it was opened from. Null closes the window, which is also what happens if
 * the id ever stops resolving — an item withdrawn from under an open window
 * closes it rather than leaving a dialog describing something that is gone.
 */
const workbenchItem = computed(() => {
  if (!workbenchId.value) return null;
  const items = garage.overview.value?.items ?? [];
  return items.find((i) => i.id === workbenchId.value) ?? null;
});

/**
 * What the workbench compares against: the fitted item in the same slot.
 *
 * Read from the item's own kind rather than from the current tab, because the two
 * can come apart — following a gate link switches to the research tab, and a
 * window left open would otherwise start comparing a hull against the equipped
 * turret. The same defect `gridItems` records having had when a third shelf
 * arrived.
 */
const workbenchCurrent = computed(() => {
  const item = workbenchItem.value;
  if (!item) return null;
  return item.kind === 'HULL' ? garage.equippedHull.value : garage.equippedTurret.value;
});

/** The other half of the loadout, so the viewer builds from a complete pair. */
const workbenchCounterpart = computed(() => {
  const item = workbenchItem.value;
  const player = garage.overview.value?.player;
  if (!item || !player) return null;
  return item.kind === 'HULL' ? player.equippedTurretId : player.equippedHullId;
});

const MODE_STORAGE_KEY = 'sow.mode';

/**
 * The chosen match mode. Same reasoning as the map above, one preference along:
 * a browser fact, never an account fact, and never on the wire as anything but
 * the id of the room it selects.
 */
const mode = ref<MatchMode>(readStoredMode());

function readStoredMode(): MatchMode {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    if (stored && MATCH_MODES.includes(stored as MatchMode)) return stored as MatchMode;
  } catch {
    // Storage can be denied outright; a preference must not take the garage down.
  }
  return DEFAULT_MATCH_MODE;
}

watch(mode, (id) => {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, id);
  } catch {
    // See above.
  }
});

const MISSION_STORAGE_KEY = 'sow.mission';
const DIFFICULTY_STORAGE_KEY = 'sow.missionDifficulty';

/**
 * The mission chosen, or null for player-versus-player.
 *
 * **Kept apart from `mode`, which is what the deploy actually carries.** The
 * three mission modes are deliberately absent from `MATCH_MODES` — that constant
 * is the server's validation list in three places — so putting one in `mode`
 * would be refused at the join and would land the player in a deathmatch on
 * mission ground. Held here, a mission choice cannot reach the wire at all,
 * which is the property that makes it safe to offer the UI ahead of the route.
 *
 * A browser preference like the map and the mode beside it, never an account
 * fact.
 */
const mission = ref<MissionKind | null>(readStoredMission());
const missionDifficulty = ref<MissionDifficulty>(readStoredDifficulty());

function readStoredMission(): MissionKind | null {
  try {
    const stored = window.localStorage.getItem(MISSION_STORAGE_KEY);
    if (stored && MISSION_KINDS.includes(stored as MissionKind)) return stored as MissionKind;
  } catch {
    // Storage can be denied outright; a preference must not take the garage down.
  }
  return null;
}

function readStoredDifficulty(): MissionDifficulty {
  try {
    const stored = window.localStorage.getItem(DIFFICULTY_STORAGE_KEY);
    if (stored && MISSION_DIFFICULTIES.includes(stored as MissionDifficulty)) {
      return stored as MissionDifficulty;
    }
  } catch {
    // See above.
  }
  return DEFAULT_MISSION_DIFFICULTY;
}

watch(mission, (id) => {
  try {
    if (id) window.localStorage.setItem(MISSION_STORAGE_KEY, id);
    else window.localStorage.removeItem(MISSION_STORAGE_KEY);
  } catch {
    // See above.
  }
});

watch(missionDifficulty, (id) => {
  try {
    window.localStorage.setItem(DIFFICULTY_STORAGE_KEY, id);
  } catch {
    // See above.
  }
});

/**
 * What went wrong opening a mission room, or null.
 *
 * A mission is the one launch that needs a **round trip before the socket**:
 * every other deploy either joins a room that already exists or lets the server
 * derive a quick-match id, whereas a mission room has to be opened first so its
 * mode and its map are set together. So this is a real failure state rather than
 * the "not built yet" placeholder it replaced, and it is shown in the same place
 * that message was.
 */
const missionError = ref<string | null>(null);
const deployBlocked = computed(() => missionError.value);

/**
 * A different choice clears the last failure.
 *
 * The failure disables the Deploy button, and `launch()` is the only thing that
 * clears it — so without this, one refused mission would leave the button dead
 * for the rest of the session, including for a deathmatch that has nothing to do
 * with the room that could not be opened. Changing any part of what would be
 * deployed is the honest moment to stop blaming the last attempt.
 */
watch([mission, missionDifficulty, mode, mapId], () => {
  missionError.value = null;
});

/**
 * Starts the chosen sortie.
 *
 * PvP is unchanged and still carries no arena id: the server resolves the
 * quick-match room from the map and the mode, which is what keeps the bare
 * Deploy button one click. A mission is opened first and then joined **by id**,
 * the same path the arena browser already uses — so the mission's mode never
 * rides a join, and there is no request anywhere that pairs a mission's rules
 * with a map of the client's choosing.
 */
async function launch(): Promise<void> {
  const kind = mission.value;
  if (!kind) {
    emit('deploy', undefined, mapId.value, mode.value);
    return;
  }
  // **The tutorial is a mission and is deliberately not opened like one.** Every
  // other mission goes through `POST /api/battle/missions`, which produces an
  // ordinary paid room — charged by `openSession`, banked by `closeSession`. The
  // tutorial is the one join that does neither, and the only thing making that
  // safe is its symmetry, so it keeps the `TUTORIAL_ARENA_ID` sentinel and this
  // branch. `CREATABLE_MISSION_KINDS` is the server's half of the same rule: the
  // route below refuses this kind outright.
  if (kind === 'tutorial') {
    emit('tutorial');
    return;
  }
  missionError.value = null;
  try {
    const { arena } = await api.createMission(kind, missionDifficulty.value);
    emit('deploy', arena.id);
  } catch (error) {
    // Never leave the player on a dead button with no reason. The room was not
    // opened, so nothing has been charged and nothing has to be unwound.
    missionError.value =
      error instanceof Error ? error.message : 'Could not open the mission room.';
  }
}

onMounted(() => {
  garage.load();

  const params = new URLSearchParams(window.location.search);
  if (params.get('topup') === 'return') {
    tab.value = 'shop';
    const orderId = params.get('order');
    const sessionId = params.get('session');
    if (orderId) {
      // Core payments order (SOWP-…): credited by the webhook, polled here.
      const poll = setInterval(async () => {
        try {
          const o = await api.depositStatus(orderId);
          if (o.credited) {
            clearInterval(poll);
            showNotice({ title: 'Diamonds credited', lines: [o.invoiceNumber ? `Invoice ${o.invoiceNumber}` : 'Thank you for your purchase'], autoDismissMs: 5000 });
            garage.load();
          } else if (['DECLINED', 'ERROR', 'EXPIRED', 'MISMATCH'].includes(o.status)) {
            clearInterval(poll);
            if (o.status !== 'EXPIRED') {
              showNotice({ title: 'Payment not completed', lines: ['No diamonds were credited. If your bank shows a charge, contact support with the order id.'], autoDismissMs: 7000 });
            }
          }
        } catch { clearInterval(poll); }
      }, 2000);
      setTimeout(() => clearInterval(poll), 120_000);
    } else if (sessionId) {
      // Legacy top-up session started before core payments went live.
      const poll = setInterval(async () => {
        try {
          const s = await api.topupSessionStatus(sessionId);
          if (s.status === 'completed') {
            clearInterval(poll);
            showNotice({ title: 'Diamonds credited', lines: [`${s.diamonds.toLocaleString('en-GB')} diamonds added`], autoDismissMs: 5000 });
            garage.load();
          } else if (s.status === 'expired') {
            clearInterval(poll);
          }
        } catch { clearInterval(poll); }
      }, 2000);
      setTimeout(() => clearInterval(poll), 120_000);
    }
    window.history.replaceState({}, '', window.location.pathname);
  }
});
</script>

<template>
  <div class="garage" :class="{ immersive, compact }" :style="{ '--bar-h': barHeight ? `${barHeight}px` : undefined }">
    <HangarView
      v-if="garage.overview.value"
      ref="hangar"
      class="stage"
      :hull-id="hangarHull?.id ?? null"
      :turret-id="hangarTurret?.id ?? null"
      :paint-id="garage.equippedPaintId.value"
      :label="`${hangarHull?.name ?? 'hull'} with ${hangarTurret?.name ?? 'turret'}`"
      :quality="hangarQuality"
      :background="!immersive"
    />
    <header ref="barEl" class="bar">
      <div class="brand">
        <BrandLogo />
      </div>

      <div id="garage-menu" class="menu-panel" :class="{ open: menuOpen }">
      <nav class="tabs" role="tablist">
        <button
          v-for="entry in tabs"
          :key="entry.id"
          type="button"
          role="tab"
          :data-test="`tab-${entry.id}`"
          :aria-selected="isTopTab(entry.id)"
          :class="{ active: isTopTab(entry.id) }"
          @click="openTopTab(entry.id)"
        >
          {{ entry.label }}
        </button>
      </nav>

        <!-- Tablet only: what the bar keeps for itself is the resources. -->
        <div class="menu-account">
          <button
            v-if="wallet"
            type="button"
            class="menu-row"
            data-test="menu-rank"
            @click="rankLadderOpen = true; menuOpen = false"
          >
            <img v-if="rankArt" class="menu-rank-art" :src="rankArt" alt="" decoding="async" />
            Rank {{ wallet.rank }} · {{ wallet.rankTitle }}
          </button>
          <button type="button" class="menu-row" data-test="menu-profile" @click="emit('profile'); menuOpen = false">
            <svg class="menu-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <circle cx="8" cy="5.1" r="2.6" fill="none" stroke="currentColor" stroke-width="1.3" />
              <path d="M2.6 14.2c0-3 2.4-4.8 5.4-4.8s5.4 1.8 5.4 4.8" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
            </svg>
            {{ session.player.value?.nickname ?? 'Profile' }}
          </button>
          <button
            type="button"
            class="menu-row"
            data-test="menu-settings"
            @click="settingsOpen = true; menuOpen = false"
          >
            <GearIcon class="menu-icon" />
            Settings
          </button>
          <button type="button" class="menu-row danger" data-test="menu-sign-out" @click="askSignOut(); menuOpen = false">
            Sign out
          </button>
        </div>
      </div>

      <div class="account">
        <FuelGauge
          v-if="wallet"
          variant="bar"
          :wallet="wallet"
          :loaded-at-ms="garage.loadedAtMs.value"
          @elapsed="garage.refresh()"
        />

        <button
          v-if="garage.overview.value"
          type="button"
          class="credits"
          data-test="credits"
          data-action="open-convert"
          :data-value="garage.credits.value"
          :title="`${formatCredits(garage.credits.value)} credits. Convert diamonds into more.`"
          @click="convertOpen = true"
        >
          {{ formatBalance(garage.credits.value) }} <CurrencyIcon kind="credits" />
        </button>
        <span v-else class="credits pending">— <CurrencyIcon kind="credits" /></span>

        <button
          v-if="wallet"
          type="button"
          class="diamonds"
          data-test="diamonds"
          data-action="open-shop-diamonds"
          :data-value="wallet.diamonds"
          :title="`${formatCredits(wallet.diamonds)} diamonds. Buy more in the shop.`"
          @click="openShop('diamonds')"
        >
          {{ formatBalance(wallet.diamonds) }} <CurrencyIcon kind="diamonds" />
        </button>
        <span v-else class="diamonds pending">— <CurrencyIcon kind="diamonds" /></span>

        <button
          v-if="wallet"
          type="button"
          class="rank embossed"
          data-test="rank"
          :title="`Rank ${wallet.rank} — ${wallet.rankTitle}. See the whole ladder.`"
          @click="rankLadderOpen = true"
        >
          <img
            v-if="rankArt"
            class="rank-art"
            :src="rankArt"
            alt=""
            data-test="rank-art"
            decoding="async"
            @error="rankArtMissing = true"
          />
          <!-- The number, not the name. See `.rank-label` in the styles. -->
          <span class="rank-label">Rank {{ wallet.rank }}</span>
        </button>
        <button
          type="button"
          class="identity"
          data-test="garage-profile-toggle"
          aria-label="Profile and account"
          title="Profile, billing and account settings"
          @click="emit('profile')"
        >
          <span v-if="!compact" class="who" data-test="nickname">{{
            session.player.value?.nickname
          }}</span>
          <svg
            class="identity-icon"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="8" cy="5.1" r="2.6" fill="none" stroke="currentColor" stroke-width="1.3" />
            <path
              d="M2.6 14.2c0-3 2.4-4.8 5.4-4.8s5.4 1.8 5.4 4.8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.3"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="ghost gear"
          data-test="garage-settings-toggle"
          :class="{ active: settingsOpen }"
          :aria-expanded="settingsOpen"
          aria-label="Settings"
          title="Graphics, audio and aim settings"
          @click="settingsOpen = !settingsOpen"
        >
          <GearIcon class="gear-icon" />
        </button>
        <button
          v-if="!compact"
          type="button"
          class="ghost embossed danger"
          data-test="sign-out"
          @click="askSignOut()"
        >
          Sign out
        </button>
      </div>

      <button
        type="button"
        class="burger"
        data-test="tabs-burger"
        aria-label="Menu"
        :aria-expanded="menuOpen"
        aria-controls="garage-menu"
        @click="menuOpen = !menuOpen"
      >
        <span class="burger-icon" :class="{ open: menuOpen }" aria-hidden="true"><i></i><i></i><i></i></span>
      </button>
    </header>

    <div v-if="garage.loading.value && !garage.overview.value" class="placeholder">
      Opening the garage…
    </div>

    <div v-else-if="garage.overview.value" class="body" :class="{ full: !showSidebar, room: immersive }">
      <aside v-if="showSidebar" class="loadout">
        <TankPreview
          :hull-id="garage.equippedHull.value?.id ?? null"
          :turret-id="garage.equippedTurret.value?.id ?? null"
          :hull-name="garage.equippedHull.value?.name ?? 'No hull'"
          :turret-name="garage.equippedTurret.value?.name ?? 'No turret'"
        />

        <section class="panel">
          <h2>Combat readout</h2>
          <p class="hint">
            Exactly what the arena will run. Base chassis values with every
            upgrade already applied.
          </p>
          <StatList :descriptors="SPEC_STATS" :values="specValues" />
        </section>

        <section v-if="lifetime" class="panel record">
          <h2>Service record</h2>
          <dl>
            <div><dt>Sorties</dt><dd>{{ lifetime.sessionsJoined }}</dd></div>
            <div><dt>Distance</dt><dd>{{ Math.round(lifetime.distanceDriven) }} m</dd></div>
            <div><dt>Time in arena</dt><dd>{{ Math.round(lifetime.secondsInArena / 60) }} min</dd></div>
            <div><dt>Credits earned</dt><dd>{{ formatCredits(lifetime.creditsEarned) }}</dd></div>
            <div v-if="wallet">
              <dt>Experience</dt>
              <dd>
                {{ formatCredits(wallet.xp) }}
                <template v-if="wallet.xpForNextRank">
                  <span class="muted">/ {{ formatCredits(wallet.xpForNextRank) }}</span>
                </template>
              </dd>
            </div>
          </dl>
        </section>

      </aside>

      <BattlePicker
        :open="pickerOpen"
        :map-id="mapId"
        :mode="mode"
        :mission="mission"
        :difficulty="missionDifficulty"
        :mission-blocked="deployBlocked"
        :can-deploy="canDeploy"
        :fuel-cost="wallet?.sortieFuelCost ?? null"
        :completed="garage.missions.value"
        @close="pickerOpen = false"
        @update:map-id="(id) => (mapId = id)"
        @update:mode="(id) => (mode = id)"
        @update:mission="(id) => (mission = id)"
        @update:difficulty="(id) => (missionDifficulty = id)"
        @deploy="launch()"
      />

      <ConfirmPurchase />

      <SpecialOfferPopup
        v-if="specialOffer && wallet"
        :offer="specialOffer"
        :diamonds="wallet.diamonds"
        :busy="garage.busyId.value !== null"
        @close="specialOffer = null"
        @view="offerView"
        @buy="offerBuy"
      />

      <RewardPopup />

      <NotificationBanner />

      <DailyQuestsWindow
        v-if="questsOpen"
        :quests="garage.quests.value"
        :busy-id="garage.busyId.value"
        :loaded-at-ms="garage.loadedAtMs.value"
        :diamonds="wallet?.diamonds ?? 0"
        @close="questsOpen = false"
        @claim="(id) => garage.claimQuest(id)"
        @reroll="rerollQuests"
        @elapsed="garage.refresh()"
      />

      <RankLadder
        v-if="rankLadderOpen"
        :ranks="garage.ranks.value"
        :wallet="wallet"
        @close="rankLadderOpen = false"
      />

      <TrainingPrompt
        v-if="trainingPromptOpen"
        @start="acceptTraining"
        @dismiss="declineTraining"
      />

      <ConvertCreditsModal
        v-if="convertOpen && wallet"
        :wallet="wallet"
        :busy-id="garage.busyId.value"
        @close="convertOpen = false"
        @convert="(diamonds) => garage.convertDiamonds(diamonds)"
      />

      <CustomiseWindow
        v-if="garage.customiseOpen.value"
        :customise="garage.customiseOpen.value"
        :credits="garage.credits.value"
        :diamonds="garage.diamonds.value"
        :busy-id="garage.busyId.value"
        @close="garage.closeCustomise()"
        @buy="(hullId, id) => garage.buyPart(hullId, id)"
        @fit="(id) => garage.equipPart(garage.customiseOpen.value!.hullId!, id)"
        @show-research="(nodeId) => { garage.closeCustomise(); showResearch(nodeId); }"
      />

      <ItemWorkbench
        v-if="workbenchItem"
        :item="workbenchItem"
        :current="workbenchCurrent"
        :counterpart-id="workbenchCounterpart"
        :credits="garage.credits.value"
        :diamonds="garage.diamonds.value"
        :busy="garage.busyId.value !== null"
        @close="workbenchId = null"
        @purchase="async (id) => { if (await buyItem(id)) workbenchId = null; }"
        @upgrade="upgradeItem"
        @install-upgrade="installUpgrade"
        @skip-upgrade="skipUpgrade"
        @prestige="prestigeItem"
        @equip="equipFromGrid"
        @show-research="(nodeId) => { workbenchId = null; showResearch(nodeId); }"
        @customise="(hullId) => { workbenchId = null; garage.openCustomise(hullId); }"
      />

      <main class="shop" :class="{ 'shop-lobby': tab === 'garage' }">
        <p v-if="garage.error.value" class="message error" data-test="garage-error">
          {{ garage.error.value }}
          <button type="button" @click="garage.dismiss()">Dismiss</button>
        </p>

        <GarageLobby
          v-if="tab === 'garage'"
          :player-key="playerKey"
          :blocked-reason="deployBlocked"
          :can-deploy="canDeploy"
          :fuel-cost="wallet?.sortieFuelCost ?? null"
          :compact="compact"
          @change-battle="pickerOpen = true"
          @customise="(hullId) => garage.openCustomise(hullId)"
          @quests="questsOpen = true"
          @reroll-quests="rerollQuests"
          @quests-elapsed="garage.refresh()"
        />

        <div v-else-if="tab === 'research'">
          <div
            v-if="researchLabLeft || researchLabFrom !== null"
            class="lab-strip"
            :class="{ live: !!researchLabLeft }"
            data-test="research-lab-strip"
          >
            <template v-if="researchLabLeft">
              <strong>Research Lab active</strong>
              <span>research started now takes half the time · {{ researchLabLeft }} left</span>
            </template>
            <template v-else>
              <strong>Research Lab</strong>
              <span>halve every research wait — boosts from {{ researchLabFrom }} <CurrencyIcon kind="diamonds" /></span>
            </template>
            <button type="button" class="lab-link" @click="openShop('boosts')">
              {{ researchLabLeft ? 'Extend' : 'See boosts' }}
            </button>
          </div>
          <ResearchTree
            :nodes="garage.research.value"
            :items="garage.overview.value.items"
            :credits="garage.credits.value"
            :busy-id="garage.busyId.value"
            @research="garage.research_"
            @skip="garage.skipResearch"
            @elapsed="garage.refresh()"
          />
        </div>

        <template v-else-if="tab === 'shop' && garage.shop.value && wallet">
        <PaymentMethodPicker
          :currency="garage.shop.value.pricing?.currency ?? 'EUR'"
          @currency-changed="garage.load()"
        />
        <DiamondShop
          :shop="garage.shop.value"
          :wallet="wallet"
          :bundles="garage.bundles.value"
          :busy-id="garage.busyId.value"
          :initial-category="shopCategory ?? undefined"
          @buy-pack="garage.buyDiamonds"
          @buy-custom-pack="(diamonds) => garage.buyCustomPack(diamonds)"
          @buy-boost="garage.buyBoost"
          @buy-bundle="garage.buyBundle"
          @refill="(units) => garage.refillFuel(units)"
          @buy-fuel-tank="garage.buyFuelTank()"
          @buy-xp="(diamonds) => garage.buyXp(diamonds)"
        />
        </template>

        <CommissionOffice v-else-if="tab === 'commissions'" />

        <div v-else-if="tab === 'cosmetics'" class="cosmetics">
          <nav class="rail" aria-label="Cosmetic sections">
            <button
              v-for="entry in cosmeticShelves"
              :key="entry.key"
              type="button"
              :data-test="`cosmetics-cat-${entry.key}`"
              :class="{ active: cosmeticCat === entry.key }"
              @click="cosmeticCat = entry.key as 'liveries' | 'titles'"
            >
              {{ entry.title }}
            </button>
          </nav>

          <section :key="cosmeticShelf.key" class="pane">
            <h3 class="shelf-label">{{ cosmeticShelf.title }}</h3>

            <div class="grid" :class="{ 'grid-titles': cosmeticShelf.key === 'titles' }">
              <NoTitleCard
                v-if="cosmeticShelf.key === 'titles'"
                :worn="wornTitleId !== null"
                :busy="garage.busyId.value !== null"
                :nickname="session.player.value?.nickname ?? ''"
                @remove="garage.removeTitle()"
              />

              <ItemCard
                v-for="item in cosmeticShelf.items"
                :key="item.id"
                :item="item"
                :current="null"
                :counterpart-id="garage.overview.value.player.equippedHullId"
                :credits="garage.credits.value"
                :diamonds="garage.diamonds.value"
                :busy="garage.busyId.value !== null"
                :nickname="session.player.value?.nickname ?? ''"
                @purchase="buyItem"
                @upgrade="upgradeItem"
        @install-upgrade="installUpgrade"
        @skip-upgrade="skipUpgrade"
                @prestige="prestigeItem"
                @show-research="showResearch"
                @customise="garage.openCustomise"
                @equip="equipFromGrid"
              />
            </div>
          </section>
        </div>

        <template v-else>
        <nav class="tank-tabs" role="tablist" aria-label="Tank parts">
          <button
            v-for="entry in tankTabs"
            :key="entry.id"
            type="button"
            role="tab"
            :data-test="`tab-${entry.id}`"
            :aria-selected="tab === entry.id"
            :class="{ active: tab === entry.id }"
            @click="tab = entry.id"
          >
            {{ entry.label }}
          </button>
        </nav>
        <div class="grid grid-mech">
          <ItemCard
            v-for="item in gridItems"
            :key="item.id"
            :item="item"
            :current="gridCurrent"
            :counterpart-id="
              tab === 'hulls'
                ? garage.overview.value.player.equippedTurretId
                : garage.overview.value.player.equippedHullId
            "
            :credits="garage.credits.value"
            :diamonds="garage.diamonds.value"
            :busy="garage.busyId.value !== null"
            :nickname="session.player.value?.nickname ?? ''"
            @purchase="buyItem"
            @upgrade="upgradeItem"
        @install-upgrade="installUpgrade"
        @skip-upgrade="skipUpgrade"
            @prestige="prestigeItem"
            @show-research="showResearch"
            @customise="garage.openCustomise"
            @equip="equipFromGrid"
            @open="(id) => (workbenchId = id)"
          />
        </div>
        </template>
      </main>
    </div>

    <div v-else class="placeholder">
      {{ garage.error.value ?? 'The garage is unavailable.' }}
    </div>

    <!-- The lobby room. Mounted here rather than inside `.body` so it survives
         every tab and does not have to be re-derived for the body grid's width
         rules — see the component's own note.

         Shown on every tab including the immersive hangar, deliberately. Hiding
         it there was the first arrangement and it was wrong: `immersive` is the
         *default* tab, so the room would have been invisible until a player
         happened to open the shop. The dock collapses to a single bar and
         remembers that, which is the better answer for somebody who wants the
         hangar bare — a choice they make once, rather than one made for them. -->
    <ChatDock />

    <SettingsPanel v-if="settingsOpen" @close="settingsOpen = false">
      <template v-if="compact" #actions>
        <div class="account-sheet" data-test="account-sheet">
          <span class="who" data-test="nickname">{{ session.player.value?.nickname }}</span>
          <button
            type="button"
            class="ghost"
            data-test="garage-profile-sheet"
            @click="emit('profile')"
          >
            Profile &amp; account
          </button>
          <button
            type="button"
            class="ghost embossed danger"
            data-test="sign-out"
            @click="askSignOut()"
          >
            Sign out
          </button>
        </div>
      </template>
    </SettingsPanel>
  </div>
</template>

<style scoped>
/*
 * ═══ The title bar's one scale factor ═══════════════════════════════════════
 *
 * Every figure in the bar — the logo's height, the tab labels, both balances,
 * the rank chip and its insignia, the gears, the fuel strip, the gaps and the
 * padding — is `calc(base * var(--bar-scale))`, and nothing in it states a size
 * any other way. That is the same discipline the 30% pass was carried out with
 * ("every figure in it was scaled together in one pass ... tuning those
 * independently is how a bar ends up looking taller without anything in it
 * looking bigger"), except that it is now one number rather than a pass.
 *
 * **The base is the pre-enlargement size, and it is the base because it fits.**
 * The bar carried the 30% pass at every width, and the measured consequence is
 * in `.tabs` below: the chrome came to ~1690 px, so the tab strip dropped onto a
 * second row on every laptop and the bar took 128 px of a short window. The
 * enlargement is not withdrawn — it is moved to the only viewport with the room
 * for it.
 *
 * `1.3` restores it, so a wide monitor draws the bar the 30% pass drew. The base
 * figures are the tuned pre-pass ones rather than today's divided by 1.3, so the
 * top tier is a hair over what that pass shipped; the alternative was thirteen
 * derived numbers nobody could re-measure.
 *
 * 2000 px is a threshold rather than a measurement, but it is checked against
 * one: a 1.3-scaled bar needs 1906 px of one row (see `.tabs` for the sum and
 * the worst case), so the tier only ever applies where the enlarged row fits.
 * Below it nothing is scaled at all, which is what makes the small end cheap to
 * reason about — one set of figures, and the phone pins the same value again so
 * a short wide window cannot pick up the tier through its width.
 */
.garage {
  --bar-scale: 1;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--steel-900);
  overflow: hidden;
}

@media (min-width: 2000px) {
  .garage {
    --bar-scale: 1.3;
  }
}

/*
 * The room, behind everything on every tab. Out of the flow so no tab
 * reserves space for it, under everything so every tab paints over it.
 */
.stage {
  position: absolute;
  inset: 0;
  border-radius: 0;
  z-index: 0;
}

/*
 * Everything else stacks above the scene. On the garage floor the content is
 * glass — the lobby manages its own containers-none/controls-auto pattern, and
 * these two wrappers would otherwise catch every drag before the canvas under
 * them ever saw it (an element with default pointer-events receives hits
 * anywhere in its box, background or none). The banners take the pointer back:
 * a Dismiss button that cannot be clicked is not a button.
 */
/*
 * **No `z-index` here, and that absence is load-bearing.**
 *
 * It used to be `z-index: 1`, which is the obvious way to say *above the scene*
 * and had a consequence nobody was looking for: a positioned element with a
 * numeric `z-index` creates a **stacking context**, and every one of the nine
 * overlays below is mounted inside this element. So their `z-index: 40`–`100`
 * was scoped *inside* a layer at 1 and could never rise above `.bar` at 3 —
 * the title bar painted over every dialog in the garage.
 *
 * What that looked like is why it survived review: the dialogs render, they are
 * legible, and the scrim dims the screen exactly as intended. Only the strip of
 * each one that happens to lie under the bar is dead, and on the battle picker
 * that strip contains the Close button, sitting directly beneath `.tabs`. A
 * player could still leave by Escape or by clicking the scrim — both of which
 * `BattlePicker` handles — so the fault reads as "that button feels
 * unresponsive" rather than as a broken layout. It was found by the e2e harness
 * instead: `closeBattlePicker` timed out with Playwright reporting `<nav
 * class="tabs"> … intercepts pointer events`, which took `chooseMap` and
 * `chooseMode` down and with them every case that names a battlefield.
 *
 * Painting above `.stage` does not need a number. `.stage` is `z-index: 0` and
 * this element comes after it in tree order, so among positioned siblings at
 * `auto`/`0` this one paints later and therefore on top — which is all the
 * original 1 was buying. Dropping it lets each overlay's own z-index mean what
 * it says against the bar, and preserves the ordering *between* them
 * (`ConfirmPurchase` at 100 over the rest at 40).
 *
 * The one thing that also stops being scoped is `ResearchTree`'s `.controls` at
 * `z-index: 7`, which now competes at the root and so sits above the bar's 3.
 * That is invisible rather than merely acceptable: those buttons are pinned to
 * the *bottom right* of the tree and the bar is at the top, so the two have no
 * geometry in common — and any dialog at 40 still covers them. Everything else
 * inside the research tree is scoped by the pan/zoom `transform`, which creates
 * a stacking context of its own.
 *
 * **Do not add a `z-index` back here.** If this element ever genuinely needs
 * one, the overlays have to move out of it in the same commit — they are
 * `position: fixed` and belong beside `.bar`, not inside the pane they cover.
 */
.body {
  position: relative;
}

.garage.immersive .body {
  pointer-events: none;
}

/* Every dialog mounted beside `main` — the picker, the confirm, the quests,
   the ladder, the customise window — takes the pointer back wholesale… */
.garage.immersive .body > * {
  pointer-events: auto;
}

/* …except the content wrapper itself, which is the pane the lobby lives in
   and must stay glass, with the lobby running its own restores inside it. */
.garage.immersive .body > .shop {
  pointer-events: none;
}

.garage.immersive .shop > .message {
  pointer-events: auto;
}

/*
 * The scrim every other tab sits on. Translucent rather than opaque on
 * purpose — the room ghosting through is what says the garage never went
 * away — and a flat wash rather than a backdrop blur, because a blur is a
 * per-frame cost over a live canvas and this one is under every grid in the
 * shop.
 */
.garage:not(.immersive) .body {
  background: rgba(11, 14, 18, 0.74);
}

/*
 * The title bar as a scrim.
 *
 * Taking the bar out of the flow is what makes "full screen" true rather than
 * nearly true — otherwise the room starts fifty pixels down and has a hard grey
 * lid, which is the framing this layout exists to remove, just smaller. A
 * gradient rather than a panel, fading to nothing at its lower edge, so there is
 * no line anywhere across the picture; the top of the scene is ceiling and
 * roof girders, which is the part of the backdrop that can afford to be dark.
 *
 * The lobby carries its own top padding to clear this. That is a number in two
 * files, which is a real cost — but the alternative is the bar reserving space,
 * and reserving space is precisely what it must not do.
 */
.garage.immersive .bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding-bottom: calc(1.4rem * var(--bar-scale));
  border-bottom: none;
  background: linear-gradient(
    180deg,
    rgba(8, 10, 13, 0.92) 0%,
    rgba(8, 10, 13, 0.6) 55%,
    rgba(8, 10, 13, 0) 100%
  );
}

.bar {
  flex: none;
  display: flex;
  align-items: center;
  /*
   * Tightened from 1.5rem when the Tutorial tab arrived: nine tabs plus the
   * brand, three currencies, the rank chip, a title, a call sign and two buttons
   * overran 1440 px, and the overflow does not clip politely — the currencies
   * landed on top of the Shop tab and `Sign out` wrapped onto two lines. The
   * strip is allowed to give way first (see `.tabs`), because a tab scrolled a
   * few pixels is legible and a control drawn over another is not.
   */
  gap: calc(1rem * var(--bar-scale));
  /*
   * Scaled with everything else in the row through `--bar-scale` — see the top
   * of this stylesheet. The row's height is the sum of its tallest child and its
   * padding, so this cannot be tuned on its own without the bar looking taller
   * with nothing in it looking bigger. Nothing here reserves space for the
   * garage tab: the bar is a scrim over it and the lobby pads by the measured
   * `--bar-h`, so growing it moves the daily-quest card down by itself.
   */
  padding: calc(0.6rem * var(--bar-scale)) calc(1.25rem * var(--bar-scale));
  border-bottom: 1px solid var(--steel-700);
  background: var(--steel-800);
  /* Not for the settings window, which is a child of `.garage` and covers the
     whole screen — it is for the bar's own children, which are positioned
     against it. */
  position: relative;
  /* So the panel draws over the tab below it — the garage tab is a full-bleed
     WebGL scene, and the immersive title bar is a scrim over it rather than a
     bar above it. */
  z-index: 3;
}

/*
 * The settings toggle: a gear among identity controls.
 *
 * Square rather than text-width, because a drawing in a button sized for a word
 * reads as a mistake, and `ghost` is sized for "Sign out". It is deliberately
 * the one control in this cluster that is a picture rather than a word: it is
 * also the one whose job is not identity, so a shape is how it tells itself
 * apart from the credits and diamonds chips, the rank badge and the sign-out
 * button beside it — and it is a real target rather than a glyph.
 *
 * Sized off `--control-h` now, the row's own common height (see `.account`),
 * rather than its own `2rem` — this was the tightest thing in the row before
 * that existed, and matching it is what makes "every header button is the
 * same height" true of the one control that is a square rather than a word.
 * The compact block below still forces 44px regardless, which is where
 * thumbs are.
 */
/*
 * `.ghost.gear`, not `.gear`, and that is load-bearing rather than tidy. The
 * button carries both classes, `.ghost` is declared further down this
 * stylesheet, and the two are the same specificity — so a bare `.gear` loses
 * `padding` to it. It did: the button kept `.ghost`'s 12px side padding, which
 * left 10.8px of content box inside a 37px square, and the icon — a flex item —
 * quietly shrank to fit. A gear at 10.8 × 23.2 px still reads as a gear in a
 * screenshot, which is why this was measured (`getBoundingClientRect`) rather
 * than looked at.
 */
/*
 * Border and background dropped here rather than removed from `.ghost`
 * itself — `.ghost` is still worn by `Sign out` and the compact sheet's
 * `Profile & account` button, both of which keep their outline. This is the
 * one control in the cluster that is a picture rather than a word, so what
 * marks it as pressable is the same spotlight glow `.identity` uses, not a
 * border around a 20px icon.
 */
.ghost.gear {
  position: relative;
  isolation: isolate;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--control-h);
  height: var(--control-h);
  padding: 0;
  border: 0;
  background: none;
  line-height: 1;
}

/*
 * The size, which is this caller's to pick — `GearIcon` sets neither, because the
 * battle HUD wants 20px inside a 44px tap target and a component that chose one
 * would be wrong in the other place. `:deep` because the drawing is a child
 * component's root and scoped styles do not otherwise cross into it.
 */
.gear :deep(.gear-icon) {
  width: calc(1.25rem * var(--bar-scale));
  height: calc(1.25rem * var(--bar-scale));
}

.gear:hover :deep(.gear-icon),
.gear:focus-visible :deep(.gear-icon),
.gear.active :deep(.gear-icon) {
  transform: rotate(30deg);
}

.gear:hover,
.gear:focus-visible {
  color: var(--steel-200);
}

.gear.active {
  color: var(--accent);
}

/* The rotation is decoration and says nothing the colour does not, so it is the
   whole of what reduced motion drops here. */
@media (prefers-reduced-motion: reduce) {
  .gear-icon {
    transition: none;
  }

  .gear:hover .gear-icon,
  .gear:focus-visible .gear-icon,
  .gear.active .gear-icon {
    transform: none;
  }
}

/* The account block is pushed to the far edge; the tabs take whatever is left
   between it and the brand, so a long nickname never wraps the navigation. */
.account {
  margin-left: auto;
  /* Never the thing that gives way: the tab strip scrolls instead. Without
     this the currencies and `Sign out` were compressed until they wrapped. */
  flex: none;
  white-space: nowrap;
}

/*
 * The mark is `ui/BrandLogo.vue`, shared with the landing page's lintel — one
 * drawing, so the header a player sees before pressing Play and the header they
 * see after it cannot disagree about the game's own name.
 *
 * Its height is stated here rather than in the component because this bar and
 * the lintel are different heights, and the face is stated here for the logo's
 * fallback: this screen's display type is Protest Guerrilla and the landing
 * page's is Archivo, deliberately, so the shared mark must not import either.
 */
.brand {
  flex: none;
  display: flex;
  /* Centred rather than baseline-aligned: the mark is a replaced element, whose
     baseline is its own bottom edge, so baseline would hang it off the bar's
     text line instead of in the middle of the bar. */
  align-items: center;
  --brand-logo-height: calc(1.5rem * var(--bar-scale));
  font-size: calc(0.92rem * var(--bar-scale));
  letter-spacing: 0.16em;
  color: #fff;
}

.sub {
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.account {
  display: flex;
  align-items: center;
  gap: calc(1rem * var(--bar-scale));
  /*
   * One height for every control in this row — the credits and diamonds
   * chips, the rank badge, the identity link, settings and the sign-out
   * ghost — so a row that used to step up and down between a bare text
   * button and a bordered one now reads as one strip. `2.25rem` is close to
   * the settings gear's own pre-existing `2rem` square, which was already
   * the tightest thing in the row; nothing shrinks; the text buttons grow a
   * few px of vertical padding they did not have before.
   */
  --control-h: calc(2.25rem * var(--bar-scale));
  /*
   * A second tier, and the one-height rule above now has a stated exception
   * rather than a quiet one.
   *
   * The rank chip and Sign out are the two controls in this row nobody presses
   * in the ordinary run of a session — one opens a ladder to look at, the other
   * ends the session — and at the shared height they were the two widest things
   * on a row whose width problem is written up under `.tabs`. Sized down
   * together, so the exception is a *pair* that agrees with itself rather than
   * two one-offs; the row still reads as a strip because everything in it is
   * centred on one line, and the pair reads as the secondary chrome it is.
   *
   * The gear keeps `--control-h`: it is a square, and a smaller square beside a
   * shorter chip is two changes reading as neither.
   */
  --control-h-sm: calc(1.7rem * var(--bar-scale));
}

/*
 * The credits readout is a button now — it opens `ConvertCreditsModal` — so
 * the browser's own button chrome is reset back to how the plain `<span>`
 * used to look. `.pending` stays a `<span>`, which needs none of this.
 */
.credits {
  height: var(--control-h);
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  font-family: ui-monospace, 'SF Mono', monospace;
  /* Matches the fuel strip, the player name and the tab labels — see `.who`
     and `.tabs button` — rather than the 1rem this and `.diamonds` used to
     stand out at. */
  font-size: calc(0.8rem * var(--bar-scale));
  color: var(--accent);
  cursor: pointer;
}

.credits:hover,
.credits:focus-visible {
  color: #f08a48;
}

/*
 * The unit used to be text — `cr` and a `💎` emoji in an `<em>` — and is a drawn
 * mark now (`CurrencyIcon`), which takes its size from this font-size and needs
 * no rule of its own. What is left is the *loading* state: the mark dims with the
 * dash beside it rather than carrying a second palette, which is why the icon
 * inherits opacity instead of colour.
 */
.credits.pending svg,
.diamonds.pending svg {
  opacity: 0.55;
}

.credits.pending,
.diamonds.pending {
  color: var(--steel-600);
}

/*
 * A button now, on the same footing as `.credits` — pressing a balance you
 * are short of opens the shelf that sells more of it (`openShop('diamonds')`
 * in the script), rather than sitting there as a number with nothing to do.
 */
.diamonds {
  height: var(--control-h);
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: calc(0.8rem * var(--bar-scale));
  color: #9fd7f5;
  cursor: pointer;
}

.diamonds:hover,
.diamonds:focus-visible {
  color: #bfe6ff;
}

span.diamonds {
  cursor: default;
}

.diamonds em {
  font-style: normal;
  font-size: 0.7rem;
}

.rank {
  height: var(--control-h-sm);
  display: flex;
  align-items: center;
  gap: calc(0.3rem * var(--bar-scale));
  padding: calc(0.15rem * var(--bar-scale)) calc(0.42rem * var(--bar-scale))
    calc(0.15rem * var(--bar-scale)) calc(0.25rem * var(--bar-scale));
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  background: none;
  font-size: calc(0.56rem * var(--bar-scale));
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--steel-400);
  cursor: pointer;
}

/*
 * **The number, not the name.**
 *
 * The chip said `Recruit`, `Major General`, `Marshal` — the rank's *title*,
 * which is the part of it that has no order. A player reading `Lieutenant
 * Colonel` cannot tell from the chip whether that is the fourth rung or the
 * fourteenth, and where they are on the ladder is the only thing a one-line
 * readout has room to answer. The number says it and takes a third of the
 * width doing it, which the row that keeps running out of it (see `.tabs`) is
 * the better for.
 *
 * The name is not lost: the button's `title` still reads `Rank 9 — Lieutenant
 * Colonel`, and the ladder this chip opens draws every rung with its title and
 * its artwork. The chip is the index; the ladder is the page.
 */
.rank-label {
  white-space: nowrap;
}

/*
 * The insignia, sized to the chip rather than over it.
 *
 * It was `1.5rem` — deliberately a little *over* the chip's line box, so the
 * badge read as a badge rather than as a bullet before the word. On a chip that
 * is now shorter overall that same figure would be the whole control, an
 * insignia with a caption stuck to it; `1.05rem` keeps the proportion the
 * original figure was reaching for against the smaller box. It is not expected
 * to be *countable* at either size — the number beside it carries the rank now,
 * and the ladder draws the artwork at 72 px.
 *
 * The art is transparent and drawn for dark chrome, so it must never go on a
 * light plate — the same warning `RankLadder.vue` carries.
 */
.rank-art {
  flex: none;
  width: calc(1.05rem * var(--bar-scale));
  height: calc(1.05rem * var(--bar-scale));
  object-fit: contain;
  display: block;
}

/* Hover/focus colour is `.embossed`'s now — see the block below `.ghost`,
   which also carries the bevel, the grime and the glare. */

.muted {
  color: var(--steel-400);
}

/*
 * One line, because the worn title that made it two is gone from this row —
 * see the note on `.identity` below. The column and its `align-items:
 * flex-end` went with the second line: a single right-aligned child needs
 * neither, and leaving them would keep the bar sized for a stacked pair it no
 * longer holds.
 */
.who {
  display: inline-flex;
  align-items: center;
  font-size: calc(0.8rem * var(--bar-scale));
  line-height: 1.1;
  color: var(--steel-200);
}

/*
 * The profile door, and the name beside it — one link now rather than a name
 * sitting next to an unrelated bordered square. The button used to carry
 * `.ghost.gear`'s chrome (a border and a background chip, sized to hold a
 * lone icon); neither belongs here once the name is inside the same control,
 * so this is its own class with none of that chrome — background is `none`,
 * border is `none`, and what tells a reader it is pressable is the same pair
 * every other text link in this app uses: colour and an underline, on hover.
 */
.identity {
  position: relative;
  isolation: isolate;
  height: var(--control-h);
  display: inline-flex;
  align-items: center;
  gap: calc(0.35rem * var(--bar-scale));
  padding: 0 calc(0.3rem * var(--bar-scale));
  border: 0;
  border-radius: 3px;
  background: none;
  cursor: pointer;
}

.identity-icon {
  flex: none;
  width: calc(1.1rem * var(--bar-scale));
  height: calc(1.1rem * var(--bar-scale));
  color: var(--steel-400);
  /* Colour only — never a transform, or "the icon does not move on hover"
     stops being true the first time this is touched again. */
  transition: color 150ms ease-out;
}

/*
 * The hover state is three separate cues, deliberately not one: the icon
 * lightens in place, the name gains an underline, and a spotlight sits
 * behind the *pair* — never a ring hugging either element on its own, which
 * is what a tight `box-shadow` or an `outline` would read as. It used to be
 * a `box-shadow` with no spread, which is ambient light in every direction
 * from a rectangle — closer to a rectangular glow than a spotlight, because
 * a shadow follows its element's own box shape. The pseudo-element below is
 * an actual ellipse, so it reads as light falling on the bar from behind the
 * control rather than as the control's own edge lit up. `.gear` shares it —
 * see `::after`, below — because it is the same cue on the same row.
 */
.identity:hover .identity-icon,
.identity:focus-visible .identity-icon {
  color: var(--steel-200);
}

.identity:hover .who,
.identity:focus-visible .who {
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

@media (prefers-reduced-motion: reduce) {
  .identity-icon {
    transition: none;
  }
}

/*
 * The spotlight itself: an oval sitting *behind* the control (`z-index: -1`,
 * contained by `isolation: isolate` on the button so it can never climb over
 * a sibling), wider than the control and taller still, so it reads as a pool
 * of light the control is standing in rather than a highlight traced around
 * it. Shared between `.identity` and `.gear` — both need `position: relative`
 * for this to lay out against the *button*, not the whole title bar, which
 * has its own `position: relative` and would otherwise catch it.
 */
.identity::after,
.gear::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 190%;
  height: 240%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.22) 0%,
    rgba(255, 255, 255, 0.09) 45%,
    transparent 72%
  );
  opacity: 0;
  z-index: -1;
  pointer-events: none;
  transition: opacity 160ms ease-out;
}

.identity:hover::after,
.identity:focus-visible::after,
.gear:hover::after,
.gear:focus-visible::after {
  opacity: 1;
}

/* The settings window is open — an accent spotlight rather than the neutral
   hover one, so the gear says *this is on* even once the pointer has moved
   away and `:hover` no longer applies. */
.gear.active::after {
  opacity: 1;
  background: radial-gradient(
    ellipse at center,
    color-mix(in srgb, var(--accent) 40%, transparent) 0%,
    color-mix(in srgb, var(--accent) 16%, transparent) 45%,
    transparent 72%
  );
}

@media (prefers-reduced-motion: reduce) {
  .identity::after,
  .gear::after {
    transition: none;
  }
}

.ghost {
  height: var(--control-h);
  display: inline-flex;
  align-items: center;
  padding: calc(0.35rem * var(--bar-scale)) calc(0.6rem * var(--bar-scale));
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: none;
  color: var(--steel-400);
  font-size: calc(0.7rem * var(--bar-scale));
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/*
 * Sign out, at the rank chip's size — the other half of the pair `--control-h-sm`
 * exists for. Scoped to `.account`, and that is not tidiness: the same three
 * classes are worn by the Sign out inside the compact settings sheet
 * (`.account-sheet`), which is a full-width control in a panel and has no
 * business shrinking because a chip in the title bar did.
 */
.account .ghost.danger {
  height: var(--control-h-sm);
  padding: calc(0.25rem * var(--bar-scale)) calc(0.5rem * var(--bar-scale));
  font-size: calc(0.6rem * var(--bar-scale));
}

/*
 * `.rank` and the sign-out `.ghost` — a plate rather than a flat rectangle,
 * declared after both so its `border-color`/`background` win the cascade at
 * equal specificity. `--btn-hot` is the colour the plate turns hot under the
 * pointer: `.rank` already turned accent on hover, so the accent is the
 * default; `.danger` (sign-out is the one destructive control in this row)
 * overrides it to the same red `.bay-remove` already uses for a delete.
 *
 * The bevel is a resting-state effect — an inset light top edge, an inset
 * dark bottom edge and a drop shadow, which is what reads as a softly domed
 * plate standing proud of the bar rather than printed on it. Noise and glare
 * are hover-only: a smudge of grime the light only catches once it lands on
 * the plate, and a diagonal glare sweeping across it while the pointer stays.
 */
.embossed {
  --btn-hot: var(--accent);
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0) 45%, rgba(0, 0, 0, 0.22) 100%),
    var(--steel-800);
  border-color: rgba(255, 255, 255, 0.14);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 -1px 0 rgba(0, 0, 0, 0.5) inset,
    0 1px 0 rgba(0, 0, 0, 0.55),
    0 2px 3px rgba(0, 0, 0, 0.32);
  transition:
    color 150ms ease-out,
    border-color 150ms ease-out,
    box-shadow 150ms ease-out;
}

.embossed.danger {
  --btn-hot: #d2544a;
}

/* The grime: a small desaturated turbulence tile, invisible until hovered. */
.embossed::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='70' height='70'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 70px 70px;
  mix-blend-mode: overlay;
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease-out;
}

/* The glare: a diagonal band parked off the left edge until hovered, then
   swept across on a loop for as long as the pointer stays. */
.embossed::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -40%;
  width: 35%;
  height: 200%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.4) 45%, transparent 88%);
  transform: translateX(-40%) rotate(8deg);
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease-out;
}

.embossed:hover,
.embossed:focus-visible {
  color: var(--btn-hot);
  border-color: var(--btn-hot);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.1) inset,
    0 -1px 0 rgba(0, 0, 0, 0.55) inset,
    0 1px 0 rgba(0, 0, 0, 0.55),
    0 2px 5px rgba(0, 0, 0, 0.4),
    0 0 calc(1rem * var(--bar-scale)) color-mix(in srgb, var(--btn-hot) 35%, transparent);
}

.embossed:hover::before,
.embossed:focus-visible::before {
  opacity: 0.45;
}

.embossed:hover::after,
.embossed:focus-visible::after {
  opacity: 1;
  animation: embossed-glare 1.4s ease-in-out infinite;
}

@keyframes embossed-glare {
  from {
    transform: translateX(-40%) rotate(8deg);
  }
  to {
    transform: translateX(420%) rotate(8deg);
  }
}

/* Pure motion, carrying no information the colour change does not already —
   dropped entirely rather than held at a fixed frame. */
@media (prefers-reduced-motion: reduce) {
  .embossed:hover::after,
  .embossed:focus-visible::after {
    animation: none;
    opacity: 0;
  }
}

.body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: 1.25rem;
  padding: 1.25rem;
  overflow: hidden;
}

/* Seven tabs hide the sidebar, and a two-column grid with one child does not
   leave the gap empty — it puts the survivor in the *first* column, which is the
   narrow one. The whole screen then renders inside 320 px with the tab strip
   clipped. */
.body.full {
  grid-template-columns: 1fr;
}

/* Only the garage floor drops the gutter: it is a room that reaches the edges of
   the window, and 1.25 rem of grey around it is the frame that layout exists to
   get rid of. Every other tab keeps it, because a grid of cards butted against
   the viewport looks like a mistake — which is why this is its own class rather
   than another rule on `.full`, now that `.full` covers six document tabs too. */
.body.room {
  padding: 0;
}

.loadout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding-right: 0.25rem;
  /* Clearance for `ChatDock`'s closed-state toggle, fixed at `left: 1rem;
     bottom: 1rem;` — this column's own scrolled content reaches the same
     corner and sat under it. Only load-bearing while `showSidebar` is true
     (the hulls/turrets shop tabs, desktop width), which is exactly where the
     chat dock is also on screen; the 900px breakpoint below drops this
     column's `overflow-y` to `visible` and stacks it in normal flow, where
     trailing padding is harmless rather than a fix for anything. */
  padding-bottom: 3rem;
}

/* The column scrolls; without this its children are squeezed to fit instead,
   and the tank preview loses most of its height. */
.loadout > * {
  flex: none;
}

.panel {
  padding: 1rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-800);
}

.panel h2 {
  margin: 0 0 0.4rem;
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.hint {
  margin: 0 0 0.9rem;
  font-size: 0.72rem;
  line-height: 1.45;
  color: var(--steel-400);
  opacity: 0.85;
}

.record dl {
  margin: 0;
  display: grid;
  gap: 0.4rem;
  font-size: 0.8rem;
}

.record dl > div {
  display: flex;
  justify-content: space-between;
}

.record dt {
  color: var(--steel-400);
}

.record dd {
  margin: 0;
  color: #fff;
  font-family: ui-monospace, 'SF Mono', monospace;
}

/*
 * The lobby fills its area and scrolls nothing; the shop is the opposite, a grid
 * that grows and scrolls. `position: relative` is what lets the lobby's messages
 * float over the scene instead of taking a row in the flex column.
 */
.shop-lobby {
  position: relative;
  gap: 0;
  overflow: hidden;
}

.shop-lobby :deep(.lobby) {
  flex: 1;
  min-height: 0;
}

.shop-lobby .message {
  position: absolute;
  top: 0.9rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  max-width: 32rem;
  backdrop-filter: blur(9px);
}

/*
 * The Tanks tab's Hulls / Turrets switch: a riveted steel plate with two
 * stencilled segments. The active segment is lit from below by a hazard strip.
 * The Commission Office rail uses the same look (`CommissionOffice.vue`).
 */
.tank-tabs {
  position: relative;
  display: inline-flex;
  align-self: flex-start;
  gap: 2px;
  padding: 3px 9px;
  border: 1px solid var(--steel-600);
  border-radius: 4px;
  background:
    radial-gradient(circle at 4px 4px, #6b7787 1px, transparent 2px),
    radial-gradient(circle at calc(100% - 4px) 4px, #6b7787 1px, transparent 2px),
    radial-gradient(circle at 4px calc(100% - 4px), #6b7787 1px, transparent 2px),
    radial-gradient(circle at calc(100% - 4px) calc(100% - 4px), #6b7787 1px, transparent 2px),
    linear-gradient(180deg, #232b35, #12161c);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 6px 14px -10px rgba(0, 0, 0, 0.7);
}

.tank-tabs button {
  position: relative;
  min-width: 6.5rem;
  min-height: 2.1rem;
  padding: 0.25rem 1rem 0.4rem;
  border: 1px solid transparent;
  border-radius: 3px;
  background: transparent;
  color: var(--steel-400);
  font-family: var(--font-display);
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease, box-shadow 120ms ease;
}

.tank-tabs button:hover {
  color: var(--steel-200);
  background: rgba(255, 255, 255, 0.03);
}

.tank-tabs button.active {
  border-color: rgba(232, 114, 44, 0.45);
  background: linear-gradient(180deg, rgba(232, 114, 44, 0.05), rgba(232, 114, 44, 0.2));
  color: #fff;
  text-shadow: 0 0 8px rgba(232, 114, 44, 0.7);
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.55), 0 0 10px rgba(232, 114, 44, 0.16);
}

.tank-tabs button.active::after {
  content: '';
  position: absolute;
  left: 14%;
  right: 14%;
  bottom: 2px;
  height: 3px;
  border-radius: 1px;
  background: repeating-linear-gradient(-45deg, var(--accent) 0 5px, #14181d 5px 10px);
  box-shadow: 0 0 6px rgba(232, 114, 44, 0.55);
}

@media (prefers-reduced-motion: reduce) {
  .tank-tabs button {
    transition: none;
  }
}

/* A phone in landscape: the switch spans the pane and keeps the 44px tap floor. */
.garage.compact .tank-tabs {
  display: flex;
  align-self: stretch;
  padding: 2px 6px;
}

.garage.compact .tank-tabs button {
  flex: 1 1 0;
  min-width: 0;
  min-height: 44px;
  font-size: 0.8rem;
}

.shop {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  overflow-y: auto;
  /* Room for the scrollbar whether or not it shows, so a long pane and a short
     one are the same width and nothing centred in them shifts on a tab switch. */
  scrollbar-gutter: stable;
}

/*
 * In the title bar now. The underline that marks the active tab is drawn on the
 * button rather than by a bottom border on the strip, so it lines up with the
 * bar's own rule instead of adding a second one a few pixels above it.
 */
.tabs {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

/*
 * Below this the bar's fixed chrome — brand, fuel strip, currencies, rank chip,
 * call sign, two buttons — eats the row, and the tabs' scroll window collapses:
 * a tab can be scrolled *under* the brand or the fuel gauge but never clear of
 * them, which is a strip no pointer can use (found by the e2e harness's 900px
 * context, where a tab was permanently unclickable). Wrapping the tabs onto
 * their own full-width row gives every tab real ground instead of asking two
 * rows of chrome to share one.
 *
 * **The threshold is the measured worst case and it moves whenever the chrome
 * does — re-measure rather than nudge it.** It has been 1100, then 1700, then
 * 1540, and is 1400 now; each figure is the one-row width of that day's bar.
 * The 1540 figure was measured against a 721px account cluster, and the cluster
 * has since shrunk to **612px** — the balances shortened to `4.6K`, the fuel
 * strip gave back everything but its count, the worn title left the row — so a
 * MacBook at its default scaling (1470 and 1512 CSS px, exactly the band
 * between the two figures) was drawing two rows of bar for a strip with over
 * 100px of room to spare. That is the same defect the 1700 → 1540 move fixed,
 * one shrink later. Measured in the running garage at `--bar-scale: 1`: 40px of
 * padding + 170 brand + 503 tabs + 612 account + 32px of gaps = **1,357px**,
 * and **~1,390px** worst case — a sixteen-character call sign, which is the
 * whole nickname budget, and a five-character balance.
 *
 * **One query covers both scale tiers, deliberately.** A media query cannot read
 * `--bar-scale`, and the ≥2000px tier does not need a second one: 1357 × 1.3 =
 * **1764px** against a 2000px viewport, and even the worst case is 1390 × 1.3 ≈
 * 1807, so the scaled row fits by construction with almost 200px to spare.
 * Below the worst case but above the query the degradation is the mild one —
 * `.tabs` is `min-width: 0` with `overflow-x: auto`, so it gives up a few
 * pixels of a 503px strip and scrolls — rather than the collapse this rule
 * exists for, which needs a shortfall of hundreds.
 */
@media (max-width: 1400px) {
  .bar {
    flex-wrap: wrap;
    /* The second row is a strip of labels, not a second row of chrome, so it
       gets a seam rather than the row's full gap — 144px of lid over a research
       tree is worth trimming where the trim costs nothing to read. */
    row-gap: 0.15rem;
    padding-bottom: 0.35rem;
  }

  .tabs {
    order: 9;
    flex-basis: 100%;
  }

  .tabs button {
    padding-top: calc(0.4rem * var(--bar-scale));
    padding-bottom: calc(0.4rem * var(--bar-scale));
  }
}

/*
 * ─── Tablets: the tab strip folds into a burger menu ─────────────────────────
 *
 * Between a phone (`.compact`, which keeps its own scrolling strip) and a laptop,
 * the strip took a second row of the bar and still scrolled. Here it is one
 * button naming the current tab, opening a panel of every tab under the bar.
 */
.burger {
  display: none;
}

/* Everywhere but a tablet, the panel is no box at all: the tab strip inside it
   lays out in the bar exactly as it did, and the account copy stays hidden. */
.menu-panel {
  display: contents;
}

.menu-account {
  display: none;
}

@media (max-width: 1180px) {
  /* The burger: a compact square at the right end of the bar. */
  .garage:not(.compact) .burger {
    order: 99;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid var(--steel-600);
    border-radius: 4px;
    background: var(--steel-800);
    color: #fff;
    cursor: pointer;
  }

  .garage:not(.compact) .burger[aria-expanded='true'] {
    border-color: var(--accent);
    background: var(--steel-700);
  }

  /* The bar keeps the resources; rank, player, settings and sign out move into
     the menu. */
  .garage:not(.compact) .account {
    margin-left: auto;
  }

  .garage:not(.compact) .account .rank,
  .garage:not(.compact) .account .identity,
  .garage:not(.compact) .account .gear,
  .garage:not(.compact) .account [data-test='sign-out'] {
    display: none;
  }

  .garage:not(.compact) .menu-panel {
    display: none;
    position: absolute;
    top: 100%;
    right: calc(1.25rem * var(--bar-scale));
    z-index: 60;
    flex-direction: column;
    gap: 0.3rem;
    width: 16rem;
    max-height: calc(100dvh - var(--bar-h, 4rem) - 1rem);
    overflow-y: auto;
    padding: 0.4rem;
    border: 1px solid var(--steel-600);
    border-radius: 0 0 6px 6px;
    background: var(--steel-800);
    box-shadow: 0 18px 32px -12px rgba(0, 0, 0, 0.75);
  }

  .garage:not(.compact) .menu-panel.open {
    display: flex;
  }

  .garage:not(.compact) .tabs {
    order: 0;
    flex-basis: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    overflow: visible;
  }

  .garage:not(.compact) .tabs button {
    min-height: 44px;
    padding: 0.6rem 0.9rem;
    border-bottom: 0;
    border-left: 3px solid transparent;
    border-radius: 3px;
    text-align: left;
  }

  .garage:not(.compact) .tabs button.active {
    border-left-color: var(--accent);
    background: var(--steel-700);
  }

  .garage:not(.compact) .menu-account {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 0.3rem;
    border-top: 1px solid var(--steel-700);
  }

  .menu-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-height: 44px;
    padding: 0.5rem 0.9rem;
    border: 0;
    border-radius: 3px;
    background: none;
    color: var(--steel-200);
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    text-align: left;
    cursor: pointer;
  }

  .menu-row:hover {
    background: var(--steel-700);
    color: #fff;
  }

  .menu-row.danger {
    color: #e0806f;
  }

  .menu-icon {
    width: 1rem;
    height: 1rem;
    flex: none;
  }

  .menu-rank-art {
    width: 1.3rem;
    height: 1.3rem;
    object-fit: contain;
  }

  /* With the strip folded away the bar is one row again. */
  .garage:not(.compact) .bar {
    flex-wrap: nowrap;
    padding-bottom: calc(0.6rem * var(--bar-scale));
  }
}

.burger-icon {
  position: relative;
  display: inline-block;
  width: 1.1rem;
  height: 0.8rem;
}

.burger-icon i {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  border-radius: 1px;
  background: currentColor;
  transition: transform 150ms ease, opacity 150ms ease, top 150ms ease;
}

.burger-icon i:nth-child(1) {
  top: 0;
}

.burger-icon i:nth-child(2) {
  top: calc(50% - 1px);
}

.burger-icon i:nth-child(3) {
  top: calc(100% - 2px);
}

.burger-icon.open i:nth-child(1) {
  top: calc(50% - 1px);
  transform: rotate(45deg);
}

.burger-icon.open i:nth-child(2) {
  opacity: 0;
}

.burger-icon.open i:nth-child(3) {
  top: calc(50% - 1px);
  transform: rotate(-45deg);
}

@media (prefers-reduced-motion: reduce) {
  .burger-icon i {
    transition: none;
  }
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tabs button {
  flex: none;
  padding: calc(0.55rem * var(--bar-scale)) calc(0.85rem * var(--bar-scale));
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--steel-400);
  /* Matches the fuel strip, the two currency chips and the player name — see
     `.who` — rather than the 0.7rem every other size in this row was tuned
     away from. */
  font-size: calc(0.8rem * var(--bar-scale));
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.tabs button:hover {
  color: var(--steel-200);
}

.tabs button.active {
  color: #fff;
  border-bottom-color: var(--accent);
}

.message {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0.8rem;
  border-radius: 3px;
  font-size: 0.82rem;
}

.message.error {
  border-left: 2px solid #c0392b;
  background: rgba(192, 57, 43, 0.12);
}

.message button {
  border: none;
  background: none;
  color: var(--steel-400);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  align-items: stretch;
  padding-bottom: 1rem;
}

/*
 * Small desktop: three across, declared rather than left to `auto-fill`.
 *
 * The sidebar takes 320px of a window like this, so `.grid`'s own 300px track
 * fits two columns short of 1280px and only reaches three past it — a gap
 * that lands exactly on the "small desktop" sizes this session's other fixes
 * are for. Declared at three rather than waiting for `auto-fill` to get there
 * on its own, the same move `.cosmetics .grid` already makes for the shelf
 * beside this one — and `ItemCard.vue`'s own small-desktop rule trims its
 * preview a little shorter, so three narrower cards do not read as three
 * columns of the same picture stretched thin.
 *
 * `:not(.compact)` because `.garage.compact .grid-mech` already answers this
 * question for a landscape phone, with its own measured figures — a width
 * query must not reach into a layout it was not measured against.
 */
@media (max-width: 100rem) {
  .garage:not(.compact) .grid-mech {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/*
 * Two across below that, not three squeezed thinner still. Three of the
 * `.grid`'s own 300px floor need 900px of content width and the sidebar
 * already spends 320 of whatever the window has, so a window in the
 * 901–1100px band was asking three cards to share less room than the fixed
 * track they were declared against — narrower than `ItemCard.vue`'s own
 * small-desktop preview was measured for. Later in the source than the
 * three-column rule above and matched at the same specificity, so it wins
 * here by cascade order the way this file's other stacked breakpoints do.
 */
@media (max-width: 68.75rem) {
  .garage:not(.compact) .grid-mech {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* The Cosmetics tab, laid out like the store: a sticky rail and one shelf. The
   rail's own rules are copied from `DiamondShop.vue` deliberately rather than
   extracted — two files agreeing on nine declarations is cheaper to read than a
   shared class nobody can find, and if a third surface ever wants a rail, *that*
   is the moment to extract one. */
.cosmetics {
  /* The store's own wide-display type factor, mirrored so the two tabs that
     share a container and a heading size cannot drift apart — see
     `--shop-scale` in `DiamondShop.vue`. Only `.shelf-label` reads it here:
     the cards are `ItemCard`s shared with the hulls and turrets shelves, and
     scaling their type from this tab would restyle three other tabs sideways. */
  --shop-scale: 1;
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  width: 100%;
  /* The store's own width cap, for the store's own reason: a catalogue that
     stretches to a 27-inch monitor is one nobody can scan. */
  max-width: clamp(min(56rem, 100%), 70vw, 100%);
  margin-inline: auto;
  padding-bottom: 3.25rem;
}

@media (min-width: 2000px) {
  .cosmetics {
    --shop-scale: 1.25;
  }
}

.cosmetics .rail {
  position: sticky;
  top: 0.25rem;
  flex: none;
  width: 10rem;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cosmetics .rail button {
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--steel-700);
  border-left: 3px solid transparent;
  border-radius: 3px;
  background: var(--steel-800);
  color: var(--steel-400);
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
}

.cosmetics .rail button:hover {
  color: var(--steel-200);
}

.cosmetics .rail button.active {
  border-left-color: var(--accent);
  background: var(--steel-700);
  color: #fff;
}

.cosmetics .pane {
  flex: 1;
  min-width: 0;
  /* A size container, so the column count below can ask the pane its own
     width — the store's `.pane` makes the same declaration for the same
     reason, and inline-size only, so the height stays free to grow.
     `align-self: stretch` comes with it as a pair: the compact layout turns
     this flex row into a column whose `align-items: flex-start` would size a
     size-contained child to its (empty) content width — the collapsed-pane
     fault written out on the store's own `.pane`. */
  align-self: stretch;
  container-type: inline-size;
}

/*
 * A declared count, never `auto-fill`'s floating one — that half of the
 * original four-across decision stands. The *number* moved when the reference
 * screen did: four across was chosen on a large display, and on a Mac 13"
 * (a ~828px pane once the cap and the rail take theirs) it made a livery card
 * 195px — names wrapping, patterns thumbnail-sized. Three across is that
 * screen's right answer (~265px, the shop's own card width beside it), and
 * the fourth column comes back only when the pane itself has the room —
 * 1100px, a threshold picked so a card's width grows monotonically with the
 * window (265 → 332 → 391 across Mac 13"/16"/a 27") instead of dipping at the
 * size where a column is added. Both shelves state the same number, and the
 * rule is scoped to `.cosmetics` because bare `.grid` is also the
 * hulls/turrets shelf, whose auto-fill track must not move.
 *
 * A livery card's pattern is cover-cropped rather than shrunk (see
 * `ItemCard.vue`'s `.pattern`), so a narrower column is a tighter crop of the
 * same texture, not lost detail; `minmax(0, ...)` is what lets a track track
 * the pane instead of fighting a pixel floor.
 */
.cosmetics .grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* **Larger**, which was the ask, and it needed a rule of its own rather than a
   bigger font size: at 0.7rem this was a caption sitting above a grid of cards
   that each carry louder type than it did, so the heading read as a label for
   the page's chrome rather than as the name of the shelf. It is now the size of
   the thing it names, with a rule under it to give it the weight a section
   heading has to carry when the page holds exactly one. */
.shelf-label {
  margin: 0 0 1.1rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--steel-700);
  font-size: calc(1.35rem * var(--shop-scale, 1));
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel-100, #eef1f4);
}

/*
 * A title card has no 3-D still and no stat list, so it is roughly half the
 * height of a hull card, and it used to run a narrower 220px track of its own
 * for that reason. The column count is shared by both shelves (`.cosmetics
 * .grid` above and the container step below), so this states the same numbers
 * rather than competing ones: a title card is shorter, not narrower, and a
 * figure of its own here would only win or lose by specificity depending on
 * the viewport.
 */
.grid-titles {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* The fourth column, when the pane itself affords it — see the count's own
   comment above for how 1100px was picked. */
@container (min-width: 1100px) {
  .cosmetics .grid,
  .grid-titles {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.placeholder {
  flex: 1;
  display: grid;
  place-items: center;
  color: var(--steel-400);
  font-size: 0.9rem;
}

@media (max-width: 900px) {
  .body {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .loadout,
  .shop {
    overflow: visible;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Compact: the garage on a phone held sideways.
 *
 * Measured at 667×375 before any of this, with `e2e/cases/garage-landscape.mjs`:
 *
 *   the title bar was 162px — **43% of the height**, in two rows
 *   `.account` was **799px wide** on a 667px screen and clipped
 *   the settings gear and Sign out sat at left=676 — **off-screen, unreachable**
 *   the hulls sidebar was 627×1078 stacked *above* the grid; first card at y=1258
 *   the garage tab did not lay out: `.floor` 0px tall, `.stats` scrolling 536px in 19px
 *
 * The last of those was this bar's own doing only in part, and it is fixed
 * elsewhere: the garage tab's four-corner compact layout lives in
 * `GarageLobby.vue`'s own compact block, which is also where the arithmetic for
 * why three stacked bands cannot happen in 298 px is written out. It takes
 * `compact` as a prop from here rather than asking `matchMedia` a second time.
 *
 * Keyed off a class rather than a bare `@media`, because `matchMedia` in the
 * script is what also drives the `v-if`s — one source of truth for both. See
 * `COMPACT_QUERY` for why the threshold is a height.
 * ═══════════════════════════════════════════════════════════════════════════ */

/*
 * One row, and the brand goes.
 *
 * The mark is the game's identity and a player mid-session knows which game they
 * are in; the row's width is the constraint, and this is the widest thing on it
 * that carries no state. Everything left is either navigation or a number the
 * player needs.
 */
/* On a phone the logo stays, at 70% of its size. */
.garage.compact .brand {
  order: -2;
  --brand-logo-height: 1.05rem;
}

/*
 * The phone is never scaled.
 *
 * `COMPACT_QUERY` is a *height*, so a wide-but-short window can match both this
 * and the 2000px width tier, and a 1.3-scaled bar on a 375px-tall screen is the
 * one place there is no room to give. Pinned rather than left to the cascade
 * because the tier above sets the property on `.garage` too, and this rule is the
 * more specific of the two only by accident of selector length.
 */
.garage.compact {
  --bar-scale: 1;
}

.garage.compact .bar {
  flex-wrap: nowrap;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  min-height: 48px;
}

/*
 * The tabs come **first** and take the room that is left.
 *
 * `.tabs` carries `order: 9` so that below 1700px it drops onto a full-width
 * second row — the documented "the tab strip gives way first", which it does by
 * taking a row of its own. On a phone that row costs 42px of 375, so this puts
 * everything back on one line. But `order: 9` then leaves the strip *last*, and
 * with `.account` at `flex: none` the primary navigation ended up pinned to the
 * right edge with **116px for 579px of content** — a five-times scroll on the
 * control a player uses most. Ordering it first and letting it take the slack
 * inverts that: the account cluster is what stops growing, and it is trimmed
 * below to make the room.
 */
.garage.compact .tabs {
  order: -1;
  flex: 1 1 auto;
  min-width: 0;
  /*
   * A fade on the trailing edge, because the strip holds 579px of tabs in ~204px
   * and clips mid-word: `TURRETS` cut after two letters reads as a broken label
   * rather than as "there is more this way". The mask says the second thing. Both
   * properties, since Safari still wants the prefix.
   */
  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 1.25rem), transparent);
  mask-image: linear-gradient(to right, #000 calc(100% - 1.25rem), transparent);
}

/*
 * …and no fade once the strip fits, or the last tab is permanently half-faded on
 * a screen with room to spare. `scrollWidth` is not available to CSS, so this is
 * the honest approximation: the mask is only worth having where the strip is
 * actually narrower than its content, which on the viewports this mode covers is
 * every phone and no tablet.
 */
@media (min-width: 900px) {
  .garage.compact .tabs {
    -webkit-mask-image: none;
    mask-image: none;
  }
}

.garage.compact .account {
  gap: 0.4rem;
  margin-left: auto;
}

/*
 * One trim now, not two, and the one that went is worth recording.
 *
 * The account cluster measured 527px even after the brand, the title and Sign
 * out had gone, which left the tab strip a quarter of the bar. Two labels were
 * dropped for it: the fuel countdown's `+1 in` prefix, and the rank chip's —
 * both on the argument that a player can infer the word from what stays, since
 * an insignia is a rank and a clock beside `9/20` is when the next one arrives.
 *
 * **The rank half of that argument expired when the chip stopped carrying a
 * title.** It was hidden because `Lieutenant Colonel` is wide; `Rank 9` is not,
 * and it is the one thing a 17 px insignia cannot say — the whole reason the
 * chip shows a number is that at this size the artwork is not countable. So the
 * label stays on a phone, and the cluster is still narrower than it was when
 * the title was showing. The fuel prefix's own argument is untouched.
 *
 * The width floor and the centring stay: they were written for an insignia-only
 * chip and they cost nothing on one that is already wider than 44 px.
 */
.garage.compact .rank {
  min-width: 44px;
  justify-content: center;
}

.garage.compact .account :deep(.strip-timer-prefix) {
  display: none;
}

/*
 * Every control on the row reaches the tap-target floor.
 *
 * Measured before: `.credits` 83×24, the rank chip 106×27, the gear 37×37 — all
 * of them under the 44px `CLAUDE.md` sets project-wide, on the row a thumb has to
 * hit while holding a phone in two hands.
 */
.garage.compact .account button,
.garage.compact .tabs button {
  min-height: 44px;
}

/*
 * ─── The rank chip keeps its desktop height ──────────────────────────────────
 *
 * A **third stated exception** to the 44px floor, beside the two in
 * `docs/mobile-garage.md`, and it was asked for: the chip stood 44px tall on a
 * phone against 27px on a laptop, and being the widest control in the cluster it
 * is the one where that slack reads — a box bolted onto a strip rather than a
 * chip in it.
 *
 * The exception is argued on the same two grounds the others are. **It is a wide
 * target** — measured 71.8px, so at 27px it is still 1,950px² of hit area
 * against the 1,936px² a compliant 44×44 square would give, which is the
 * quantity the rule is actually protecting. And **a mis-tap costs nothing**: the
 * chip opens the rank ladder, a read-only sheet with its own close button, so
 * the worst outcome is one extra tap. That is the distinction the floor is worth
 * bending for and a Deploy button's would not be.
 *
 * Worth knowing before treating this as a slippery slope: this row **already**
 * fails the floor on the other axis and always has. `garage-landscape` reports
 * `.credits` at 36×44, `.diamonds` at 28×44 and `.identity` at 27×44 — every one
 * of them narrower than 44 because the strip has no width to give — so the rule
 * in force here has been "reachable, not square" for some time. That report is
 * diagnostic and not asserted, which is why none of it fails a case; the chip now
 * appears in it as `rank BUTTON 72x27`, which is the exception being visible
 * rather than hidden.
 *
 * `align-items: center` on `.account` already centres it, so this only removes
 * the padding a taller box was adding.
 *
 * **The selector is `.account button.rank`, not `.rank`, and that is arithmetic
 * rather than style.** The floor above it is
 * `.garage.compact .account button` — three classes, the scope attribute and a
 * *type* selector, so `(0, 4, 1)` once Vue has scoped it. A plain
 * `.garage.compact .rank` is `(0, 4, 0)` and **loses**, silently: the chip stayed
 * 44px and nothing said why. Confirmed off CDP's own matched-rule list rather
 * than counted by eye, which is how it was got wrong the first time. Writing the
 * exception through the same `.account button` it is an exception *to* buys the
 * point back and says out loud which rule is being overridden.
 */
.garage.compact .account button.rank {
  min-height: var(--control-h-sm);
  height: var(--control-h-sm);
}

.garage.compact .gear {
  width: 44px;
  min-width: 44px;
  height: 44px;
}

/*
 * The account block, in the settings window's `actions` slot on a phone.
 *
 * The window is a centred dialog that caps and scrolls itself, so this no longer
 * has to say anything about height or overflow — the two rules that used to be
 * here were `max-height` and `overflow-y` on a *static* wrapper whose only
 * overflowing child was positioned absolutely, so neither did anything.
 *
 * Wraps rather than shrinks: three items in ~340px of a landscape phone's width
 * is a call sign compressed to nothing beside two buttons, and the call sign is
 * the one part that cannot be shortened.
 */
.account-sheet {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--steel-700);
}

.account-sheet .who {
  color: var(--steel-200);
  font-size: 0.85rem;
  font-weight: 600;
}

.account-sheet .ghost {
  min-height: 44px;
}

/*
 * ─── The hulls and turrets shelves, one column wide ──────────────────────────
 *
 * `.grid`'s standing track is `minmax(300px, 1fr)`, which on a 627 px content
 * area is two columns of 313 px — and 313 px is the width at which a card that
 * carries a picture *and* six compared stats cannot fit in the 300 px of height
 * this viewport has. Measured before this: two columns, eleven rows, **666 px
 * per card**, so under half a card was ever on screen.
 *
 * One column instead, and the card turns horizontal to use it — the picture into
 * a fixed left gutter, everything else in the column beside it. The reasoning,
 * the arithmetic and what it cost is written out at the end of `ItemCard.vue`,
 * beside the rules that do it; this is only the container's half of the
 * decision.
 *
 * Stated as a 30 rem track rather than a bare `1fr`, so it is still an
 * `auto-fill` and still answers the width it is given: 627 px and 804 px — the
 * two phone viewports this mode covers — both take one column, and a short
 * *desktop* window wide enough for two horizontal cards would get two rather
 * than one card stretched across a monitor. A `1fr` would have made the column
 * count a constant, which is the kind of thing that is right until somebody
 * plays in a 1400×500 window.
 *
 * ── Why the selector names `grid-mech` and not `grid` ─────────────────────
 *
 * It was written against the bare `.grid` first, which is the obvious spelling
 * and was wrong: `.grid` is *also* the Cosmetics shelf's container, one
 * stylesheet away and in the same file, so the 30 rem minimum track landed on
 * the livery cards as well. Measured at 667×375 on that tab: the shelf collapsed
 * to a single 480 px column, and `.pane`, `.grid` and every `.card` reached
 * right = 680 of a 667 px viewport. The box that swallowed it was `.body` —
 * `scrollWidth` 700 against `clientWidth` 667, so 33 px of track past its own
 * edge — and because that box is `overflow-x: hidden`, the 13 px hanging off each
 * card was **cut off** rather than reachable: no bar, and nothing a finger could
 * drag back. At 844×390 nothing was clipped and the shelf still dropped from
 * three columns to one, which is the quieter half of the same fault.
 *
 * What makes it worth a paragraph is that **`garage-landscape` passed
 * throughout**. Its sideways assertion read the *document's* overflow and the box
 * that had grown was three levels inside it, so the guard built to catch exactly
 * this reported 0. That case now asks every element whether it is cutting one of
 * its own children off sideways — it fails on this rule put back, verified by
 * injecting it into the running page — and a compact rule that has to name one
 * shelf names that shelf, so the next one cannot reach a neighbour's grid by
 * sharing a class with it.
 */
.garage.compact .grid-mech {
  grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  gap: 0.6rem;
  padding-bottom: 0.6rem;
}

/*
 * ─── The Cosmetics tab, on a phone held sideways ─────────────────────────────
 *
 * Measured before this block at 667×375: the rail's 10rem column left the shelf
 * a 447px strip beside 180px of chrome — a third of the width spent on two
 * buttons — and the grid behind it ran to 10,424px of scroll. The rail turns
 * horizontal, which is the same answer the title bar's tab strip gives one level
 * up: navigation across the top, content taking the whole width below it.
 *
 * **Not sticky, and that is a reversal** (owner instruction, 2026-08-22): it
 * shipped stuck for the desktop rule's own reason — the shelves are long, and
 * hunting for the rail after every scroll is friction — and on a phone that
 * trade is backwards: a stuck 50px row over ~300px of content height is a sixth
 * of the screen spent on navigation for the whole session, over the one
 * dimension a landscape phone has none of. `position: static` overrides the
 * desktop sticky, so the row scrolls away with the shelf; the solid backdrop
 * and the z-index went with it, because both existed *because* it was stuck.
 * The row still scrolls sideways (`overflow-x`) if its buttons outgrow the
 * screen.
 *
 * `DiamondShop.vue` carries the same block for the store's own rail, copied
 * rather than extracted, exactly as the desktop rules already are ("two files
 * agreeing on nine declarations is cheaper to read than a shared class nobody
 * can find").
 */
.garage.compact .cosmetics {
  flex-direction: column;
  gap: 0.6rem;
}

.garage.compact .cosmetics .rail {
  position: static;
  width: 100%;
  flex-direction: row;
  overflow-x: auto;
  gap: 0.3rem;
  padding: 0.3rem 0;
}

/*
 * The active mark moves to the bottom edge, because the left edge of a button in
 * a *row* is a seam between neighbours rather than the reading edge — the same
 * geometry that puts the title bar's own active mark on `border-bottom`.
 */
.garage.compact .cosmetics .rail button {
  flex: none;
  min-height: 44px;
  border-left-width: 1px;
  border-bottom: 3px solid transparent;
}

.garage.compact .cosmetics .rail button.active {
  border-left-color: var(--steel-700);
  border-bottom-color: var(--accent);
}

/*
 * No shelf heading on a phone, and it is a duplicate rather than a cut: with the
 * rail horizontal the active button sits directly above where the heading was,
 * carrying the identical word — LIVERIES over LIVERIES — and the pair cost ~60px
 * of a 300px content area saying one thing twice. The store's own `h3`s stay
 * (trimmed, in `DiamondShop.vue`), because its shelf names differ from its rail
 * labels — a pane there holds several shelves.
 */
.garage.compact .shelf-label {
  display: none;
}

/*
 * Two columns of livery cards rather than one 577px giant. A livery card is its
 * pattern at 8/5, so a full-width card is ~420px tall against 300px of screen —
 * not one whole card visible. At ~284px the pattern is still a pattern (it is
 * cover-cropped, not shrunk detail) and two cards share a row, which is what a
 * shelf is for. This rule outweighs both desktop counts (`.cosmetics .grid`
 * and `.grid-titles`) by specificity, so both shelves answer two columns here.
 */
.garage.compact .cosmetics .grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.6rem;
}


/* The Research Lab strip above the tree. */
.lab-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.9rem;
  margin: 0 0 0.6rem;
  padding: 0.5rem 0.8rem;
  border: 1px solid var(--steel-600, #3a4350);
  border-radius: 4px;
  background: rgba(11, 13, 17, 0.7);
  font-size: 0.8rem;
  color: var(--steel-200, #c4ccd6);
}

.lab-strip strong {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
}

.lab-strip.live {
  border-color: var(--accent);
}

.lab-strip.live strong {
  color: var(--accent);
}

.lab-link {
  margin-left: auto;
  padding: 0.3rem 0.8rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: transparent;
  color: var(--accent);
  font: inherit;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.lab-link:hover {
  background: var(--accent);
  color: #0b0d11;
}
</style>